import React, { useEffect, useState, useCallback } from "react";
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import SuperAppButton from "../SuperAppButton";
import { IoMdClose } from "react-icons/io";
import { MdEdit, MdFileCopy } from "react-icons/md";
import { FaCirclePlay } from "react-icons/fa6";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  Autocomplete,
  Tooltip,
  Popover,
  IconButton,
  Chip,
  Box,
  LinearProgress,
  Button,
} from "@mui/material";

import CustomTextEditor from "./CustomInputTextEditor";

import JsonViewer from "./CustomJsonViewerEditor";
import InfoIcon from "@mui/icons-material/Info";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { jsonrepair } from 'jsonrepair';


const CustomToolTip = ({ text, direction }) => {
  return (
    <Tooltip placement={direction || undefined} title={text}>
      <InfoIcon />
    </Tooltip>
  );
};

function convertSchemaToSimplified(input) {
  // Base case: if no input or not an object, return empty object
  if (!input || typeof input !== "object") {
    return {};
  }

  const result = {};

  // Iterate through each key in the input schema
  for (const key of Object.keys(input)) {
    const schema = input[key];

    // Check if schema has a type
    if (!schema || !schema.type) {
      continue;
    }

    // Handle based on type
    switch (schema.type) {
      case "string":
        // For string type, create a simple key-value pair with empty string
        result[key] = "";
        break;

      case "object":
        // For object type, check if properties exist and recurse
        if (schema.properties) {
          result[key] = convertSchemaToSimplified(schema.properties);
        } else {
          result[key] = {};
        }
        break;

      case "array":
        // For array type, check if items exist and recurse
        if (schema.items) {
          result[key] = [convertSchemaToSimplified(schema.items)];
        } else {
          result[key] = [];
        }
        break;

      default:
        // For other types, assign an empty object
        result[key] = {};
        break;
    }
  }

  return result;
}

function getFullString(inputString, mainData) {
  try {
    const parsedInput =
      typeof inputString === "string" ? JSON.parse(inputString) : inputString;
    let finalOutput = "";

    for (const item of parsedInput) {
      if (item.type === "text" && item.text) {
        finalOutput += item.text;
      } else if (item.type === "mention" && item.attrs && item.attrs.id) {
        const resolvedValue = item.attrs.id;
        finalOutput += resolvedValue !== undefined ? resolvedValue : "";
      }
    }

    return finalOutput || "No valid data processed";
  } catch (error) {
    return inputString || `Please Add your Details.`;
  }
}

const CustomNode = ({ data }) => {
  return (
    <div
      style={{
        background: "#2A2C4B",
        border: "1px solid #4d35ff",
        borderRadius: "8px",
        padding: "10px",
        width: "200px",
        color: "white",
        position: "relative",
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: "#4d35ff", width: "10px", height: "10px" }}
      />
      <Typography style={{ fontWeight: "bold", fontSize: "14px" }}>
        {data.name}
      </Typography>
      <Typography style={{ fontWeight: "bold", fontSize: "12px" }}>
        Method: {data?.method || "Get"}
      </Typography>
      <Typography
        style={{
          fontWeight: "bold",
          fontSize: "12px",
          textTransform: "capitalize",
        }}
      >
        {data.name !== "Main API" && (
          <>Looping: {(data?.isLooping ? "TRUE" : "FALSE") || "FALSE"}</>
        )}
      </Typography>
      <Typography
        style={{ fontSize: "12px", color: "#ccc", wordBreak: "break-all" }}
      >
        URL: {getFullString(data.url) || "No URL"}
      </Typography>
      <div
        style={{
          position: "absolute",
          top: "5px",
          right: "5px",
          display: "flex",
          gap: "5px",
          justifyContent: "end",
        }}
      >
        <IconButton
          type="button"
          onClick={() => data.onTest(data.path)}
          style={{ color: "white", padding: 0 }}
          disabled={!data?.isData}
        >
          <FaCirclePlay size={16} color={data?.isData ? "#4D35FF" : "#fff"} />
        </IconButton>
        <IconButton
          onClick={data.onEdit}
          style={{ color: "white", padding: 0 }}
        >
          <MdEdit size={16} />
        </IconButton>
        <IconButton
          onClick={() => data.onCopy(data.path)}
          style={{ color: "white", padding: 0 }}
        >
          <MdFileCopy
            size={16}
            //   type="button"

            //   style={{ padding: '5px', fontSize: '12px' }}
          />
        </IconButton>
        {data.path.length > 0 && (
          <IconButton
            onClick={() => data.onRemove(data.path)}
            style={{ color: "white", padding: 0 }}
          >
            <IoMdClose size={16} />
          </IconButton>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: "#4d35ff", width: "10px", height: "10px" }}
      />
    </div>
  );
};

const nodeTypes = { custom: CustomNode };

function findMentionsLooping(inputString, mainData, index, selectedPath) {
  try {
    const parsedInput =
      typeof inputString === "string" ? JSON.parse(inputString) : inputString;

    function resolvePath(obj, path, idx) {
      const parts = path
        .replace(/\[(\d+)\]/g, (match, p1) => `.${idx}`)
        .split(".");
      return parts.reduce((acc, part) => acc && acc[part], obj);
    }

    let finalOutput = "";

    for (const item of parsedInput) {
      if (item.type === "text" && item.text) {
        finalOutput += item.text.trim();
      } else if (item.type === "mention" && item.attrs && item.attrs.id) {
        if (item.attrs.id.startsWith(selectedPath)) {
          const resolvedValue = resolvePath(
            mainData,
            item.attrs.id,
            index
          )?.trim();
          finalOutput += resolvedValue !== undefined ? resolvedValue : "";
        } else {
          const resolvedValue = resolvePath(mainData, item.attrs.id, index);
          finalOutput += resolvedValue !== undefined ? resolvedValue : "";
        }
      }
    }

    return finalOutput || null;
  } catch (error) {
    return `Error processing input: ${error.message}`;
  }
}

function findMentions(inputString, mainData) {
  try {
    const parsedInput =
      typeof inputString === "string" ? JSON.parse(inputString) : inputString;

    function resolvePath(obj, path) {
      const parts = path.replace(/\[(\d+)\]/g, ".$1").split(".");
      return parts.reduce((acc, part) => acc && acc[part], obj);
    }

    let finalOutput = "";

    for (const item of parsedInput) {
      if (item.type === "text" && item.text) {
        finalOutput += item.text;
      } else if (item.type === "mention" && item.attrs && item.attrs.id) {
        const resolvedValue = resolvePath(mainData, item.attrs.id);
        finalOutput += resolvedValue !== undefined ? resolvedValue : "";
      }
    }

    return finalOutput || "No valid data processed";
  } catch (error) {
    return `Error processing input: ${error.message}`;
  }
}

function getValueFromPath(path, obj) {
  const parts = path.replace(/\[(\d+)\]/g, ".$1").split(".");
  return parts.reduce((acc, part) => acc && acc[part], obj);
}

function getArrayPaths(obj, currentPath = "@", seenPaths = new Set()) {
  let paths = [];
  if (Array.isArray(obj)) {
    const pathEntry = { id: currentPath, label: currentPath };
    if (!seenPaths.has(currentPath)) {
      seenPaths.add(currentPath);
      paths.push(pathEntry);
    }
    obj.forEach((item, index) => {
      const itemPath = `${currentPath}[${index}]`;
      paths = paths.concat(getArrayPaths(item, itemPath, seenPaths));
    });
  } else if (typeof obj === "object" && obj !== null) {
    Object.keys(obj).forEach((key) => {
      const keyPath = currentPath === "@" ? key : `${currentPath}.${key}`;
      paths = paths.concat(getArrayPaths(obj[key], keyPath, seenPaths));
    });
  }
  return paths;
}

const KeyValueEditor = ({
  pathPrefix,
  headers,
  params,
  payload,
  onUpdateArrayItem,
  onAddArrayItem,
  onRemoveArrayItem,
  response,
  suggestions,
  setAlertDetails,
}) => {
  const [sections, setSections] = useState([]);

  useEffect(() => {
    let updated = [
      {
        label: "Headers",
        array: headers,
        path: [...pathPrefix, "headers"],
        name: "header",
      },
      {
        label: "Params",
        array: params,
        path: [...pathPrefix, "params"],
        name: "params",
      },
      {
        label: "Payload",
        array: payload,
        path: [...pathPrefix, "payload"],
        name: "payload",
      },
    ];
    setSections(updated);
  }, [headers, pathPrefix, payload, params]);

  return (
    <div style={{ marginBottom: "20px", padding: "10px" }}>
      {sections.map((section, index) => (
        <Accordion
          key={`section-${section?.label}-${pathPrefix?.join("-")}`}
          style={{
            backgroundColor: index % 2 === 0 ? "#202345" : "#1A1C37",
            color: "white",
            marginBottom: "10px",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon style={{ color: "white" }} />}
          >
            <Typography variant="h6">{section?.label}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                width: "100%",
              }}
            >
              <>
                {Array.isArray(section?.array) &&
                  section?.array.map((item, idx) => (
                    <div
                      key={`kv-${section.path.join("-")}-${idx}`}
                      style={{
                        marginBottom: "8px",
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr auto auto",
                        alignItems: "center",
                        gap: "35px",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <Typography style={{ color: "white" }}>Key:</Typography>
                        <div style={{ flex: 1 }}>
                          <CustomTextEditor
                            prompt={item.key}
                            handleTitleChange={(e) => {
                              onUpdateArrayItem(
                                section.path,
                                idx,
                                "key",
                                e.target.value
                              );
                              onUpdateArrayItem(
                                section.path,
                                idx,
                                "path",
                                e.target.value
                              );
                            }}
                            sampleData={response}
                          />
                          {/* <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {suggestions(section?.name?.toLowerCase()).map((suggestion, sIdx) => (
                            <Chip
                              key={`suggestion-key-${section.path.join('-')}-${idx}-${sIdx}`}
                              label={suggestion.label}
                              onClick={() => {
                                onUpdateArrayItem(section.path, idx, 'key', suggestion.value);
                                onUpdateArrayItem(section.path, idx, 'path', suggestion.value);
                              }}
                              sx={{ backgroundColor: '#4d35ff', color: 'white', '&:hover': { backgroundColor: '#6b5bff' } }}
                            />
                          ))}
                        </Box> */}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <Typography style={{ color: "white" }}>
                          Value:
                        </Typography>
                        <div style={{ flex: 1 }}>
                          <CustomTextEditor
                            prompt={item.value}
                            handleTitleChange={(e) =>
                              onUpdateArrayItem(
                                section.path,
                                idx,
                                "value",
                                e.target.value
                              )
                            }
                            sampleData={response}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <Typography style={{ color: "white" }}>
                          Path:
                        </Typography>
                        <div style={{ flex: 1 }}>
                          <CustomTextEditor
                            prompt={item.path}
                            handleTitleChange={(e) =>
                              onUpdateArrayItem(
                                section.path,
                                idx,
                                "path",
                                e.target.value
                              )
                            }
                            sampleData={response}
                          />
                        </div>
                      </div>
                      <label
                        style={{
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={item.isDefault}
                          onChange={(e) =>
                            onUpdateArrayItem(
                              section.path,
                              idx,
                              "isDefault",
                              e.target.checked
                            )
                          }
                          style={{ accentColor: "white" }}
                        />
                        isDefault
                      </label>
                      <SuperAppButton
                        type="button"
                        onClick={() => onRemoveArrayItem(section.path, idx)}
                        style={{
                          padding: "8px",
                          minWidth: "30px",
                          alignItems: "center",
                          justifyContent: "center",
                          display: "flex",
                          backgroundColor: "#4d35ff",
                        }}
                      >
                        <IoMdClose size={16} />
                      </SuperAppButton>
                    </div>
                  ))}
                {/* Add suggestion chips for Headers section only */}
                {section && (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}
                  >
                    {suggestions(section?.name).map((suggestion, sIdx) => (
                      <Chip
                        key={`suggestion-add-header-${section.path.join(
                          "-"
                        )}-${sIdx}`}
                        label={`Add ${suggestion.label}`}
                        onClick={() => {
                          // Add a new header item with the suggestion's value as the key

                          onAddArrayItem(section.path, {
                            key: suggestion.label,
                            value: suggestion.value, // Default empty value
                            path: suggestion.label, // Set path to key for consistency
                            isDefault: false, // Default isDefault value
                          });
                        }}
                        sx={{
                          backgroundColor: "#4d35ff",
                          color: "white",
                          "&:hover": { backgroundColor: "#6b5bff" },
                        }}
                      />
                    ))}
                  </Box>
                )}
                <SuperAppButton
                  type="button"
                  onClick={() => onAddArrayItem(section.path)}
                >
                  Add {section.label} Item
                </SuperAppButton>
              </>

              {section.name == "payload" && (
                <>
                  {/* Custom json importing */}
                  <h6>Add your own payload</h6>
                  <div style={{ height: "200px" }}>
                    {console.log("section?.[0]?.json", section)}
                    <CustomTextEditor
                      handleTitleChange={(e) => {
                        onUpdateArrayItem(
                          section.path,
                          0,
                          "json",
                          e.target.value
                        );

                        let newJson = e.target.value;
                        console.log("🚀 ~ newJson:", newJson);
                        if (newJson != "") {
                          try {
                            newJson = JSON.parse(newJson);
                            let jsonString = "";
                            newJson.map((jsonItem) => {
                              if (jsonItem.type === "mention") {
                                jsonString += jsonItem?.attrs.label;
                              } else {
                                jsonString += jsonItem?.text;
                              }
                              jsonString = jsonString.trim();
                              try {
                                JSON.parse(jsonString);
                              } catch (error) {
                                console.log(
                                  "🚀 ~ jsonStringerror:",
                                  jsonString,
                                  error
                                );
                                //   setAlertDetails({
                                //    message: `Not a valid JSON: ( ${jsonString} ). Please Try Again`,
                                //    severity: "error",
                                //    status: true,
                                //    title: "Not a valid JSON - "
                                //  });
                              }
                            });
                          } catch (error) {}
                        }
                      }}
                      prompt={section?.array?.[0]?.json}
                      isValidate={true}
                      setAlertDetails={setAlertDetails}
                      sampleData={response}
                    />
                  </div>
                </>
              )}
            </div>
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
};

async function callAllApi(
  apiData,
  mergedResponses,
  setMergedResponses,
  llm_data,
  setProgress,
  setIsLoading
) {
  function findMentions(inputString, mainData) {
    try {
      let parsedInput = [];
      try {
        if (typeof inputString === "string") {
          parsedInput = JSON.parse(inputString);
        } else {
          parsedInput = [{ text: inputString, id: inputString }];
        }
      } catch (error) {
        parsedInput = [{ text: inputString, id: inputString, type: "text" }];
      }

      function resolvePath(obj, path) {
        const parts = path.replace(/\[(\d+)\]/g, ".$1").split(".");
        return parts.reduce((acc, part) => {
          if (acc == null) return undefined;
          if (Array.isArray(acc) && /^\d+$/.test(part)) {
            const index = parseInt(part, 10);
            if (index < acc.length) return acc[index];
            return undefined;
          }
          return acc[part];
        }, obj);
      }

      let finalOutput = "";
      for (const item of parsedInput) {
        if (item.type === "text" && item.text) {
          finalOutput += item.text;
        } else if (item.type === "mention" && item.attrs && item.attrs.id) {
          const resolvedValue = resolvePath(mainData, item.attrs.id);
          finalOutput +=
            resolvedValue !== undefined ? String(resolvedValue) : "";
        }
      }
      return finalOutput || null;
    } catch (error) {
      console.error("🚀 ~ findMentions ~ error:", error.message);
      setIsLoading(false);
      return `Error processing input: ${error.message}`;
    }
  }

  function getValueFromPath(path, obj) {
    const parts = path.replace(/\[(\d+)\]/g, ".$1").split(".");
    console.log("🚀 ~ getValueFromPath ~ parts:", parts, obj);
    return parts.reduce((acc, part) => {
      if (acc == null) {
        return undefined;
      }
      if (Array.isArray(acc) && /^\d+$/.test(part)) {
        const index = parseInt(part, 10);
        if (index < acc.length) {
          return acc[index];
        } else {
          return undefined;
        }
      }

      return acc[part];
    }, obj);
  }

  function findMentionsLooping(
    inputString,
    mainData,
    index,
    selectedPath,
    isPath = false,
    isString = false
  ) {
    
    let isPathStatus = isPath;

    try {
      let parsedInput = [];
      if (inputString == "") {
        throw new Error("Invalid string");
      }
      try {
        if (typeof inputString === "string") {
          parsedInput = JSON.parse(inputString);
        } else {
          parsedInput = [{ text: inputString, id: inputString }];
        }
      } catch (error) {
        console.log("🚀 ~ findMentionsLooping ~ error:", error)
        parsedInput = [{ text: inputString, id: inputString, type: "text" }];
      }

          
console.log("🚀 ~ findMentionsLooping ~ parsedInput:", selectedPath,parsedInput)

      function resolvePath(obj, path, idx) {
        const replacedPath = path.replace(/\[(\d+)\]/g, `.${idx}`);
        const parts = replacedPath.split(".");

        return parts.reduce((acc, part) => {
          if (acc == null) {
            return undefined;
          }
          if (Array.isArray(acc) && /^\d+$/.test(part)) {
            const index = parseInt(part, 10);
            if (index < acc.length) {
              return acc[index];
            } else {
              return undefined;
            }
          }

          return acc[part];
        }, obj);
      }

      let finalOutput = "";

      for (const item of parsedInput) {
        if (item.type === "text" && item.text) {
          if (isPathStatus) {
            let newPath = item?.text?.startsWith(selectedPath)
              ? item.text
              : selectedPath + "." + item.text;
            const resolvedValue = resolvePath(mainData, newPath, index);

            finalOutput += resolvedValue;
          } else {
            finalOutput += item.text.trim();
          }
        } else if (item.type === "mention" && item.attrs && item.attrs.id) {
          let newPath = "";
          if (isPath) {
            newPath = item.attrs.id?.startsWith(selectedPath)
              ? item.attrs.id
              : selectedPath + "." + item.attrs.id;
          } else {
            newPath = item.attrs.id;
          }

          if (item.attrs.id.startsWith(selectedPath)) {
            const resolvedValue = resolvePath(mainData, newPath, index);
            console.log("🚀 ~ findMentionsLooping ~ resolvedValue:1", resolvedValue)
            finalOutput += isString
              ? '"' + resolvedValue + '"'
              : resolvedValue !== undefined
              ? String(resolvedValue)
              : "";
          } else {
            const resolvedValue = resolvePath(mainData, newPath, index);
            console.log("🚀 ~ findMentionsLooping ~ resolvedValue:2", resolvedValue)
            finalOutput += isString
              ? '"' + resolvedValue + '"'
              : resolvedValue !== undefined
              ? String(resolvedValue)
              : "";
          }
        }
      }

      return finalOutput || null;
    } catch (error) {
      console.error("🚀 ~ findMentionsLooping ~ error:", error.message);
      return inputString || null;
    }
  }
function sanitizeJsonLikeString(s) {
  if (typeof s !== "string") return s;
  let out = s.trim();

  // Keep only between the first "{" and the last "}"
  const first = out.indexOf("{");
  const last  = out.lastIndexOf("}");
  if (first !== -1 && last !== -1 && last > first) {
    out = out.slice(first, last + 1);
  }

  // Common stitching fixes:
  // 1) Replace `}{` between object chunks with `},{`
  out = out.replace(/}\s*{/g, "},{");

  // 2) Remove a stray "}" that appears just before a comma+quote key
  //    e.g.  ... "xcvxc"},"searchTypes"  ->  ..."xcvxc","searchTypes"
  out = out.replace(/}\s*,\s*"/g, ', "');

  // 3) Remove trailing commas before } or ]
  out = out.replace(/,\s*([}\]])/g, "$1");

  // 4) Balance braces if needed
  const opens  = (out.match(/{/g) || []).length;
  const closes = (out.match(/}/g) || []).length;
  if (opens > closes) {
    out += "}".repeat(opens - closes);
  } else if (closes > opens) {
    // remove extra closing braces from the end
    let diff = closes - opens;
    while (diff-- > 0) {
      out = out.replace(/}\s*$/, "");
    }
    out += "}";
  }

  return out;
}
  // function convertToKeyValueObject(
  //   array,
  //   loopContext = {},
  //   index,
  //   selectedPath
  // ) {
  //   try {
  //     const result = {};
  //     if (Array.isArray(array)) {
  //       for (const item of array) {
  //         const keyObj = findMentionsLooping(
  //           item.key,
  //           { ...mergedResponses, ...loopContext },
  //           index,
  //           selectedPath
  //         );
  //         let jsonData = findMentionsLooping(
  //           item.json,
  //           { ...mergedResponses, ...loopContext },
  //           index,
  //           selectedPath,
  //           false,
  //           true
  //         );
  //         console.log("🚀 ~ jsonDatajsonDatajsonData:", jsonData, item.json,
  //           { ...mergedResponses, ...loopContext },
  //           index,
  //           selectedPath,
  //           false,
  //           true);

  //         try {
  //           let newvalll = jsonData;
  //           console.log("🚀 ~ jsonData1:", newvalll);
  //           jsonData = JSON.parse(newvalll);
  //           if (jsonData) {
  //             console.log("🚀 ~ jsonData12:", jsonData);

  //             return jsonData;
  //           }
  //         } catch (error) {
  //           console.log("🚀 ~jsonDatajsonDatajsonData error:", jsonData, error);
  //           jsonData = null;
  //         }

  //         const valueObj = findMentionsLooping(
  //           item.value,
  //           { ...mergedResponses, ...loopContext },
  //           index,
  //           selectedPath
  //         );
  //         const isDefault = item?.isDefault;
  //         const pathValue = !isDefault
  //           ? findMentionsLooping(
  //               item.path,
  //               { ...mergedResponses, ...loopContext },
  //               index,
  //               selectedPath,
  //               true
  //             )
  //           : valueObj;
  //         result[keyObj] = valueObj ? valueObj : pathValue;
  //       }

  //       return result;
  //     } else {
  //       return null;
  //     }
  //   } catch (error) {
  //     return null;
  //   }
  // }


function convertToKeyValueObject(array, loopContext = {}, index, selectedPath) {
  try {
    const result = {};
    if (!Array.isArray(array)) return null;

    for (const item of array) {
      const keyObj = findMentionsLooping(
        item.key,
        { ...mergedResponses, ...loopContext },
        index,
        selectedPath
      );

      let jsonData = findMentionsLooping(
        item.json,
        { ...mergedResponses, ...loopContext },
        index,
        selectedPath,
        false,
        true
      );
      
      // 🔧 Sanitize before parsing
      
      try {
        console.log("🚀 ~ convertToKeyValueObject ~ jsonData:", jsonData)
        const sanitized = jsonrepair(jsonData)
        console.log("🚀 ~ convertToKeyValueObject ~ sanitized:", sanitized)
        const parsed = JSON.parse(sanitized);
        // console.log("🚀 ~ convertToKeyValueObject ~ sanitized:", sanitized)
        console.log("🚀 ~ convertToKeyValueObject ~ sanitizedparsed:", parsed)
        // const parsed = sanitized
        console.log("🚀 ~ convertToKeyValueObject ~ sanitized:", parsed)
        if (parsed) {
          // This branch is for payload "json" blocks that are full objects;
          // if that's your intent, return the parsed object immediately.
          return parsed;
        }
      } catch (error) {
        console.log("convertToKeyValueObject: JSON parse failed", { original: jsonData, error });
      }

      const valueObj = findMentionsLooping(
        item.value,
        { ...mergedResponses, ...loopContext },
        index,
        selectedPath
      );

      const isDefault = item?.isDefault;
      const pathValue = !isDefault
        ? findMentionsLooping(
            item.path,
            { ...mergedResponses, ...loopContext },
            index,
            selectedPath,
            true
          )
        : valueObj;

      result[keyObj] = valueObj ? valueObj : pathValue;
    }

    return result;
  } catch (error) {
    console.log("convertToKeyValueObject: outer error", error);
    return null;
  }
}


  async function callSingleApi(apiNode, path = [], shouldFetch = true) {
    console.log("🚀 ~ callSingleApi ~ apiNode:", apiNode);
    const apiName =
      path.length === 0
        ? "Main API"
        : `Sub-API ${path.filter((k) => !isNaN(k)).join(".")}`;
    setProgress(`Processing ${apiName}`);

    if (!apiNode.url) {
      return null;
    }

    const finalHeaders = convertToKeyValueObject(
      apiNode.headers || [],
      {},
      0,
      "input_parameters"
    );
    const finalParams = convertToKeyValueObject(
      apiNode.params || [],
      {},
      0,
      "input_parameters"
    );
    const finalPayload = convertToKeyValueObject(
      apiNode.payload || [],
      {},
      0,
      "input_parameters"
    );
    console.log("🚀 ~ callSingleApi ~ finalPayload:", finalPayload);
    let finalUrl = findMentions(apiNode.url, mergedResponses || {});

    if (Object.keys(finalParams).length > 0) {
      const queryString = new URLSearchParams(finalParams).toString();
      finalUrl = `${finalUrl}${
        finalUrl.includes("?") ? "&" : "?"
      }${queryString}`;
    }

    if (!shouldFetch) {
      return {
        method: apiNode.method || "GET",
        url: finalUrl,
        headers: finalHeaders,
        params: finalParams,
        payload: finalPayload,
        responsePath: apiNode.responsePath || "",
      };
    }

    try {
      const fetchOptions = {
        method: apiNode.method || "GET",
        headers: finalHeaders,
      };

      if (apiNode.method !== "GET" && Object.keys(finalPayload).length > 0) {
        fetchOptions.body = JSON.stringify(finalPayload);
        fetchOptions.headers["Content-Type"] = "application/json";
      }

      const requestData = {
        method: apiNode.method || "GET",
        url: finalUrl,
        headers: finalHeaders,
        params: finalParams || [],
        payload: finalPayload || [],
        responsePath: apiNode.responsePath || "",
      };

      const response = await fetch("/api/call", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        console.error(
          "🚀 ~ callSingleApi ~ HTTP error, status:",
          response.status
        );
        return { error: `HTTP error! status: ${response.status}` };
      }

      const data = await response.json();

      return data;
    } catch (err) {
      console.error("🚀 ~ callSingleApi ~ error:", err.message);
      return { error: err.message };
    }
  }

  async function handleLoopApiCall(
    apiNode,
    path,
    loopPath,
    maxLoops,
    mergedResponses,
    subApiIndex,
    prevIndex
  ) {
    console.log("🚀 ~ handleLoopApiCallapiNode:", apiNode);
    const apiName =
      path.length === 0
        ? "Main API"
        : `Sub-API ${path.filter((k) => !isNaN(k)).join(".")}`;
    setProgress(`Processing Loop for ${apiName}`);
    const pathData = getValueFromPath(loopPath, mergedResponses);
    console.log("🚀 ~ pathData:", pathData, loopPath, mergedResponses);

    let parsedArray;
    try {
      parsedArray = Array.isArray(pathData)
        ? pathData
        : typeof pathData === "string"
        ? JSON.parse(pathData)
        : [];
    } catch (e) {
      console.error(
        "🚀 ~ handleLoopApiCall ~ error parsing array data:",
        e.message
      );
      return { data: [], error: `Failed to parse path data: ${e.message}` };
    }

    if (!Array.isArray(parsedArray)) {
      console.error(
        "🚀 ~ handleLoopApiCall ~ selected path does not point to an array"
      );
      return { data: [], error: "Selected path does not point to an array" };
    }
    console.log("🚀 ~ loopContext:", parsedArray);

    const results = [];
    let newCombinedData = mergedResponses || {};
    const parts = loopPath.replace(/\[(\d+)\]/g, ".$1").split(".");

    for (let i = 0; i < Math.min(maxLoops, parsedArray.length); i++) {
      setProgress(
        `Processing Loop ${i + 1}/${Math.min(
          maxLoops,
          parsedArray.length
        )} for ${apiName}`
      );
      const loopContext = { [loopPath]: parsedArray[i] };
      const combinedContext = { ...mergedResponses, ...loopContext };

      const finalUrl = findMentionsLooping(
        apiNode.url,
        combinedContext,
        i,
        loopPath
      );
      console.log("🚀 ~ handleLoopApiCallfinalUrl:", finalUrl);
      const finalHeaders = convertToKeyValueObject(
        apiNode.headers || [],
        loopContext,
        i,
        loopPath
      );
      const finalParams = convertToKeyValueObject(
        apiNode.params || [],
        loopContext,
        i,
        loopPath
      );
      const finalPayload = convertToKeyValueObject(
        apiNode.payload || [],
        loopContext,
        i,
        loopPath
      );

      let urlWithParams = finalUrl;
      if (apiNode.method === "GET" && Object.keys(finalParams).length > 0) {
        const queryString = new URLSearchParams(finalParams).toString();
        urlWithParams = `${finalUrl}${
          finalUrl.includes("?") ? "&" : "?"
        }${queryString}`;
      }

      try {
        const fetchOptions = {
          method: apiNode.method || "GET",
          headers: finalHeaders,
        };

        if (apiNode.method !== "GET" && Object.keys(finalPayload).length > 0) {
          fetchOptions.body = JSON.stringify(finalPayload);
          fetchOptions.headers["Content-Type"] = "application/json";
        }

        const requestData = {
          method: apiNode.method || "GET",
          url: urlWithParams,
          headers: finalHeaders,
          params: finalParams || [],
          payload: finalPayload || [],
          responsePath: apiNode.responsePath || "",
        };

        const response = await fetch("/api/call", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        });

        if (!response.ok) {
          console.error(
            "🚀 ~ handleLoopApiCall ~ loop",
            i,
            "HTTP error, status:",
            response.status
          );
          results.push({ error: `HTTP error! status: ${response.status}` });
        } else {
          const data = await response.json();
          let allkeys = `['${parts.join("']['")}']`;
          let gettingResult = null;

          for (let j = 0; j <= parts.length; j++) {
            let part = parts[j];
            if (parts.length === j) {
              if ("loopData" in gettingResult[i]) {
                gettingResult[i][`loopData${prevIndex}`] = data;
              } else {
                gettingResult[i][`loopData`] = data;
              }
            } else {
              if (!newCombinedData?.[part]) {
                newCombinedData[part] = {};
              }
              gettingResult = gettingResult?.[part] || newCombinedData[part];
            }
          }
          results.push(data);
        }
      } catch (err) {
        console.error(
          "🚀 ~ handleLoopApiCall ~ loop",
          i,
          "error:",
          err.message
        );
        results.push({ error: err.message });
      }
    }

    return results;
  }

  let ancestors = "";
  async function recursiveApiCall(
    apiNode,
    path = [],
    subApiIndex = 0,
    shouldFetch = true,
    allResponse = null,
    prevIndex
  ) {
    const apiName =
      path.length === 0
        ? "Main API"
        : `Sub-API ${
            path.filter((k) => !isNaN(k)).join(".") || subApiIndex + 1
          }`;
    setProgress(`Processing ${apiName}`);
    ancestors += prevIndex ? `${subApiIndex}_${prevIndex}` : `_${subApiIndex}`;

    let result;
    const allResponses = allResponse || {
      llm_data: llm_data || {},
      main: null,
      subApis: null,
    };

    let mainData = null;

    if (!apiNode.isLooping && !apiNode.selectedPath) {
      const apiResult = await callSingleApi(apiNode, path, shouldFetch);
      result = { data: apiResult, subApis: [] };
      mainData = apiResult;
      allResponses.main = result;
    }

    if (apiNode.isLooping && apiNode.selectedPath) {
      const loopResult = await handleLoopApiCall(
        apiNode,
        path,
        apiNode.selectedPath,
        parseInt(apiNode.loopCount, 10),
        allResponses,
        subApiIndex,
        ancestors
      );
      result = { data: loopResult, subApis: [] };
      if (loopResult.error) {
        result.error = loopResult.error;
      }
    }

    for (let [index, subApi] of (apiNode.subApis || []).entries()) {
      const subResult = await recursiveApiCall(
        subApi,
        [...path, "subApis", index],
        index,
        shouldFetch,
        allResponses,
        subApiIndex
      );
      if (subResult) {
        result.subApis.push(subResult);
      }
    }

    if (path.length === 0) {
      setMergedResponses((prev) => ({
        ...prev,
        main: {
          data: result.data,
        },
        subApis: result.subApis,
      }));
    }

    setProgress("Finalizing API calls...");
    return result || { data: null, subApis: [] };
  }

  const result = await recursiveApiCall(apiData, [], 0, true);
  const allResponses = {
    input_parameters: llm_data || {},
    main: { data: result?.data ? result?.data : result },
    // subApis: result.subApis
  };

  setProgress("API calls completed");
  return allResponses;
}

export function schemaToEndParamsFlat(schema, options = {}) {
  const {
    preferredDefaults = {},
    useSchemaDefault = true,
    inferFromDescription = true,
    fallback = "",
    includeMissingRequired = true,

    // NEW:
    keyStrategy = "leaf",          // 'leaf' -> emit "count", 'path' -> emit "a[].b.count"
    collision = "first",           // 'first' | 'last' | 'suffix' | 'array'
  } = options;

  const PRIMS = new Set(["string", "number", "boolean"]);
  const out = {};

  const leafKeyFromPath = (fullPath) =>
    fullPath.replace(/\[\]/g, "").split(".").pop();

  const applyCollision = (k, v) => {
    if (!(k in out)) { out[k] = v; return; }
    if (collision === "first") return;
    if (collision === "last") { out[k] = v; return; }
    if (collision === "array") {
      const prev = out[k];
      out[k] = Array.isArray(prev) ? [...prev, v] : [prev, v];
      return;
    }
    if (collision === "suffix") {
      let i = 2, kk = `${k}_${i}`;
      while (kk in out) { i += 1; kk = `${k}_${i}`; }
      out[kk] = v;
    }
  };

  const inferFromDesc = (desc, t) => {
    if (!inferFromDescription || typeof desc !== "string") return undefined;
    let m = desc.match(/Example:\s*"([^"]+)"/i);
    if (m && m[1] != null) return castToken(m[1], t);
    m = desc.match(/Example:\s*([^\s,.;]+)/i);
    if (m && m[1] != null) return castToken(m[1], t);
    return undefined;
  };

  const castToken = (token, t) => {
    if (t === "number" && !isNaN(Number(token))) return Number(token);
    if (t === "boolean" && /^(true|false)$/i.test(token)) return /^true$/i.test(token);
    return token;
  };

  const pickDefault = (fullPath, node, t) => {
    const leafKey = leafKeyFromPath(fullPath);

    // Allow preferredDefaults to match either the full path or the leaf key
    if (Object.prototype.hasOwnProperty.call(preferredDefaults, fullPath)) {
      return preferredDefaults[fullPath];
    }
    if (Object.prototype.hasOwnProperty.call(preferredDefaults, leafKey)) {
      return preferredDefaults[leafKey];
    }

    if (useSchemaDefault && node && Object.prototype.hasOwnProperty.call(node, "default")) {
      return node.default;
    }
    const inferred = inferFromDesc(node?.description, t);
    if (inferred !== undefined) return inferred;

    // final fallback
    if (t === "number") return fallback === "" ? "" : fallback;
    if (t === "boolean") return fallback === "" ? "" : fallback;
    return fallback; // string
  };

  const emit = (fullPath, node, t) => {
    const key = keyStrategy === "leaf" ? leafKeyFromPath(fullPath) : fullPath;
    const val = pickDefault(fullPath, node, t);
    applyCollision(key, val);
  };

  const addMissingRequired = (node, path) => {
    if (!includeMissingRequired) return;
    if (!node || !Array.isArray(node.required)) return;
    const props = node.properties && typeof node.properties === "object" ? node.properties : {};
    for (const reqKey of node.required) {
      if (!props[reqKey]) {
        const reqPath = path ? `${path}.${reqKey}` : reqKey;
        // unknown type; treat as string for fallback
        emit(reqPath, {}, "string");
      }
    }
  };

  const visit = (node, path) => {
    if (!node || typeof node !== "object") return;

    const nodeType = Array.isArray(node.type) ? node.type[0] : node.type;

    if (nodeType === "object" || (node.properties && typeof node.properties === "object")) {
      const props = node.properties || {};
      for (const [name, prop] of Object.entries(props)) {
        const nextPath = path ? `${path}.${name}` : name;
        visit(prop, nextPath);
      }
      addMissingRequired(node, path);
      return;
    }

    if (nodeType === "array" || node.items) {
      const items = node.items || {};
      const itemType = Array.isArray(items.type) ? items.type[0] : items.type;

      if (PRIMS.has(String(itemType).toLowerCase())) {
        // array of primitive → emit the array key (leaf will be the parent name)
        const arrPath = `${path}[]`;
        const t = String(itemType).toLowerCase();
        emit(arrPath, items, t);
      } else {
        // array of objects → dive into items
        visit(items, `${path}[]`);
      }
      return;
    }

    const t = String(nodeType || "").toLowerCase();
    if (PRIMS.has(t)) {
      emit(path, node, t);
    }
  };

  visit(schema, "");
  return out;
}




/************************************************
 * 4) Main pruner (headers/params + payload + subApis)
 ************************************************/
// function updateSchemaWithMentions(schema1, schema2) {
//   // --- deep copy so original schema1 is untouched ---
//   const newSchema1 = JSON.parse(JSON.stringify(schema1));

//   // ---------- helpers ----------
//   const safeParse = (x) => {
//     try { return typeof x === "string" ? JSON.parse(x) : x; } catch { return null; }
//   };

//   // Collect mention ids when schema2 is a plain object (your case):
//   // e.g., { searchTerm, searchType, start, count, currency } =>
//   //       Set(["input_parameters.searchTerm", "input_parameters.searchType", ...])
//   const collectIdsFromPlainObject = (obj, prefix = "input_parameters.") => {
//     const ids = new Set();
//     if (!obj || typeof obj !== "object") return ids;

//     const walk = (o, base = "") => {
//       if (Array.isArray(o)) {
//         o.forEach((v, i) => walk(v, base)); // arrays don't change the id path for mentions
//         return;
//       }
//       if (o && typeof o === "object") {
//         for (const k of Object.keys(o)) {
//           const nextBase = base ? `${base}.${k}` : k;
//           // we only need top-level keys for your mention convention:
//           ids.add(`${prefix}${nextBase}`);
//           walk(o[k], nextBase);
//         }
//         return;
//       }
//       // primitives: nothing to add
//     };

//     walk(obj, "");
//     return ids;
//   };

//   // Collect mention ids when schema2 is a schema-like object
//   const collectIdsFromSchemaLike = (schemaLike) => {
//     const ids = new Set();
//     const sections = ["headers", "params", "payload"];
//     const fields = ["json", "key", "value", "path"];

//     const collectIdsDeep = (nodeLike) => {
//       if (!nodeLike) return;
//       if (Array.isArray(nodeLike)) { nodeLike.forEach(collectIdsDeep); return; }
//       if (typeof nodeLike === "object") {
//         if (nodeLike.type === "mention" && nodeLike.attrs?.id) {
//           ids.add(String(nodeLike.attrs.id));
//         }
//         for (const v of Object.values(nodeLike)) {
//           if (v && typeof v === "object") collectIdsDeep(v);
//         }
//       }
//     };

//     for (const section of sections) {
//       const arr = Array.isArray(schemaLike?.[section]) ? schemaLike[section] : [];
//       for (const item of arr) {
//         for (const field of fields) {
//           if (!(field in item)) continue;
//           const nodes = safeParse(item[field]);
//           collectIdsDeep(nodes);
//         }
//       }
//     }
//     return ids;
//   };

//   // Unified collector that detects schema2 shape automatically
//   const collectValidMentionIdsFromSchema2 = (s2) => {
//     // If s2 has headers/params/payload arrays, treat it as schema-like
//     if (
//       (s2 && typeof s2 === "object") &&
//       (Array.isArray(s2.headers) || Array.isArray(s2.params) || Array.isArray(s2.payload))
//     ) {
//       return collectIdsFromSchemaLike(s2);
//     }
//     // Otherwise treat as plain input object
//     return collectIdsFromPlainObject(s2, "input_parameters.");
//   };

//   // Remove a mention whose id isn't valid AND the immediately preceding text node
//   const prunePayloadJsonByMentions = (payloadJson, validIds) => {
//     let nodes;
//     try {
//       nodes = typeof payloadJson === "string" ? JSON.parse(payloadJson) : payloadJson;
//     } catch {
//       return payloadJson; // not parseable → keep original
//     }
//     if (!Array.isArray(nodes)) return payloadJson;

//     const out = [];
//     for (let i = 0; i < nodes.length; i++) {
//       const n = nodes[i];

//       if (n && n.type === "mention") {
//         const id = String(n?.attrs?.id || "");
//         if (!validIds.has(id)) {
//           // Drop this mention and the immediately previous text node if present
//           const last = out[out.length - 1];
//           if (last && last.type === "text") out.pop();
//           continue;
//         }
//         out.push(n);
//         continue;
//       }

//       // keep non-mention nodes
//       out.push(n);
//     }

//     try {
//       return JSON.stringify(out);
//     } catch {
//       return payloadJson;
//     }
//   };

//   // ---------- main ----------
//   const validIds = collectValidMentionIdsFromSchema2(schema2);

//   if (Array.isArray(newSchema1.payload)) {
//     newSchema1.payload = newSchema1.payload.map((block) => {
//       if (!block || block.json == null) return block;
//       return { ...block, json: prunePayloadJsonByMentions(block.json, validIds) };
//     });
//   }

//   return newSchema1; // always a new object
// }

function updateSchemaWithMentions(schema1, schema2) {
  // --- deep copy so original schema1 is untouched ---
  const newSchema1 = JSON.parse(JSON.stringify(schema1));

  // ---------- helpers ----------
  const safeParse = (x) => {
    try { return typeof x === "string" ? JSON.parse(x) : x; } catch { return null; }
  };

  // Turn "node-array JSON" fields like item.value = "[{...}]" into a JS array of nodes
  const getNodesFromField = (field) => {
    const nodes = safeParse(field);
    return Array.isArray(nodes) ? nodes : [];
  };

  // Collect mention ids from a "nodes array" (tip-tap-ish: [{type:"text"...},{type:"mention", attrs:{id}}])
  const collectMentionIdsFromNodes = (nodes) => {
    const ids = new Set();
    const walk = (n) => {
      if (!n) return;
      if (Array.isArray(n)) { n.forEach(walk); return; }
      if (typeof n === "object") {
        if (n.type === "mention" && n.attrs?.id) {
          ids.add(String(n.attrs.id));
        }
        for (const v of Object.values(n)) {
          if (v && typeof v === "object") walk(v);
        }
      }
    };
    walk(nodes);
    return ids;
  };

  // Collect mention ids when schema2 is a plain object (your case)
  const collectIdsFromPlainObject = (obj, prefix = "input_parameters.") => {
    const ids = new Set();
    if (!obj || typeof obj !== "object") return ids;

    const walk = (o, base = "") => {
      if (Array.isArray(o)) {
        o.forEach((v) => walk(v, base)); // arrays don't change the id path
        return;
      }
      if (o && typeof o === "object") {
        for (const k of Object.keys(o)) {
          const nextBase = base ? `${base}.${k}` : k;
          // We only need top-level-ish keys for your mention convention:
          ids.add(`${prefix}${nextBase}`);
          walk(o[k], nextBase);
        }
        return;
      }
      // primitives: nothing to add
    };

    walk(obj, "");
    return ids;
  };

  // Collect mention ids when schema2 is a schema-like object (with headers/params/payload arrays)
  const collectIdsFromSchemaLike = (schemaLike) => {
    const ids = new Set();
    const sections = ["headers", "params", "payload"];
    const fields = ["json", "key", "value", "path"];

    const collectIdsDeep = (nodeLike) => {
      if (!nodeLike) return;
      if (Array.isArray(nodeLike)) { nodeLike.forEach(collectIdsDeep); return; }
      if (typeof nodeLike === "object") {
        if (nodeLike.type === "mention" && nodeLike.attrs?.id) {
          ids.add(String(nodeLike.attrs.id));
        }
        for (const v of Object.values(nodeLike)) {
          if (v && typeof v === "object") collectIdsDeep(v);
        }
      }
    };

    for (const section of sections) {
      const arr = Array.isArray(schemaLike?.[section]) ? schemaLike[section] : [];
      for (const item of arr) {
        for (const field of fields) {
          if (!(field in item)) continue;
          const nodes = getNodesFromField(item[field]);
          collectIdsDeep(nodes);
        }
      }
    }
    return ids;
  };

  // Unified collector that detects schema2 shape automatically
  const collectValidMentionIdsFromSchema2 = (s2) => {
    // If s2 has headers/params/payload arrays, treat it as schema-like
    if (
      (s2 && typeof s2 === "object") &&
      (Array.isArray(s2.headers) || Array.isArray(s2.params) || Array.isArray(s2.payload))
    ) {
      return collectIdsFromSchemaLike(s2);
    }
    // Otherwise treat as plain input object
    return collectIdsFromPlainObject(s2, "input_parameters.");
  };

  // ====== NEW: prune PARAMS items that reference invalid mentions ======
  // Rule: keep a params item if it has no mentions OR all of its mentions are in validIds.
  const pruneParamsByMentions = (paramsArr, validIds) => {
    if (!Array.isArray(paramsArr)) return paramsArr;

    const FIELDS = ["key", "value", "path", "json"];
    const isAllMentionsValid = (item) => {
      let allOK = true;
      for (const f of FIELDS) {
        if (!(f in item)) continue;
        const nodes = getNodesFromField(item[f]);
        const ids = collectMentionIdsFromNodes(nodes);
        for (const id of ids) {
          if (!validIds.has(String(id))) {
            allOK = false; break;
          }
        }
        if (!allOK) break;
      }
      return allOK;
    };

    return paramsArr.filter((item) => isAllMentionsValid(item));
  };

  // ====== UPDATED: prune PAYLOAD.json by dropping entire top-level keys with no valid mention inside ======
  // Strategy:
  //   1) Convert nodes -> single string with sentinel tokens for VALID mentions only: ⟦id⟧
  //      (invalid mentions are dropped at serialization stage)
  //   2) On that string, parse the ROOT object and remove any top-level "key: value"
  //      whose value contains ZERO sentinel tokens.
  //   3) Convert the resulting string back to nodes: text nodes + mention nodes for ⟦id⟧
  const SENT_L = "⟦"; // sentinel open
  const SENT_R = "⟧"; // sentinel close

  const nodesToStringWithValidMentions = (nodes, validIds) => {
    const parts = [];
    for (const n of Array.isArray(nodes) ? nodes : []) {
      if (!n) continue;
      if (n.type === "mention") {
        const id = String(n?.attrs?.id || "");
        // Keep only VALID mentions; invalid ones are dropped entirely here
        if (validIds.has(id)) parts.push(`${SENT_L}${id}${SENT_R}`);
      } else if (n.type === "text") {
        parts.push(n.text || "");
      }
    }
    return parts.join("");
  };

  const stringToNodesBySentinel = (str) => {
    const out = [];
    let i = 0;
    while (i < str.length) {
      const open = str.indexOf(SENT_L, i);
      if (open === -1) {
        const tail = str.slice(i);
        if (tail) out.push({ type: "text", text: tail });
        break;
      }
      const before = str.slice(i, open);
      if (before) out.push({ type: "text", text: before });

      const close = str.indexOf(SENT_R, open + SENT_L.length);
      if (close === -1) {
        // unmatched sentinel -> treat as plain text
        out.push({ type: "text", text: str.slice(open) });
        break;
      }
      const id = str.slice(open + SENT_L.length, close);
      out.push({
        type: "mention",
        attrs: { id, label: `${id} `, mentionSuggestionChar: "@" },
      });

      i = close + SENT_R.length;
    }
    return out;
  };

  // Utility: find matching closing bracket/brace while honoring quotes/escapes.
  const findBalancedEnd = (s, startIdx) => {
    const opener = s[startIdx];
    const closer = opener === "{" ? "}" : "]";
    let depth = 0;
    let inStr = false;
    let esc = false;
    for (let i = startIdx; i < s.length; i++) {
      const ch = s[i];
      if (inStr) {
        if (esc) { esc = false; continue; }
        if (ch === "\\") { esc = true; continue; }
        if (ch === "\"") { inStr = false; continue; }
        continue;
      }
      if (ch === "\"") { inStr = true; continue; }
      if (ch === opener) depth++;
      else if (ch === closer) {
        depth--;
        if (depth === 0) return i; // index of matching closer
      }
    }
    return -1; // not found
  };

  const removeTopLevelKeysWithoutMentions = (s) => {
    // Find root object braces
    const rootStart = s.indexOf("{");
    if (rootStart === -1) return s; // not an object; return as-is
    let inStr = false;
    let esc = false;
    let curly = 0;
    let bracket = 0;

    // First, find the end of root object (matching })
    let rootEnd = -1;
    for (let i = 0; i < s.length; i++) {
      const ch = s[i];
      if (inStr) {
        if (esc) { esc = false; continue; }
        if (ch === "\\") { esc = true; continue; }
        if (ch === "\"") { inStr = false; continue; }
        continue;
      }
      if (ch === "\"") { inStr = true; continue; }
      if (ch === "{") { curly++; if (curly === 1 && rootStart === -1) {} }
      else if (ch === "}") { curly--; if (curly === 0 && i >= rootStart) { rootEnd = i; break; } }
      else if (ch === "[") { bracket++; }
      else if (ch === "]") { bracket--; }
    }
    if (rootEnd === -1) return s;

    // Parse properties at curlyLevel==1 / bracketLevel==0 / not in string
    let i = rootStart + 1;
    let lastEmit = 0;
    let result = "";

    // Tracks the last comma at top-level so that removal can strip preceding or following comma cleanly
    let lastTopComma = rootStart;

    const skipWS = () => { while (i < rootEnd && /\s/.test(s[i])) i++; };

    while (i < rootEnd) {
      skipWS();
      if (i >= rootEnd) break;
      if (s[i] === ",") { lastTopComma = i; i++; continue; }
      if (s[i] !== "\"") {
        // ill-formed, bail out (emit the rest)
        result += s.slice(lastEmit);
        return result;
      }

      // key name
      const keyStart = i; // '"'
      i++;
      let key = "";
      let esc2 = false;
      while (i < rootEnd) {
        const ch = s[i++];
        if (esc2) { esc2 = false; key += ch; continue; }
        if (ch === "\\") { esc2 = true; continue; }
        if (ch === "\"") break;
        key += ch;
      }
      skipWS();
      if (s[i] !== ":") { result += s.slice(lastEmit); return result; }
      i++; // past ':'
      skipWS();

      // value begins at i
      let valueStart = i;
      let valueEnd = -1;
      let containsValidMention = false;

      const peek = s[i];
      if (peek === "{" || peek === "[") {
        valueEnd = findBalancedEnd(s, i);
        if (valueEnd === -1) { result += s.slice[lastEmit]; return result; }
        // Quick scan for any sentinel in [valueStart, valueEnd]
        containsValidMention = s.slice(valueStart, valueEnd + 1).includes(SENT_L);
        i = valueEnd + 1;
      } else if (peek === "\"") {
        // string value: walk to closing quote
        i++; let esc3 = false;
        while (i < s.length) {
          const ch = s[i++];
          if (esc3) { esc3 = false; continue; }
          if (ch === "\\") { esc3 = true; continue; }
          if (ch === "\"") break;
        }
        valueEnd = i - 1; // index of closing quote
        containsValidMention = s.slice(valueStart, valueEnd + 1).includes(SENT_L);
      } else if (s.startsWith(SENT_L, i)) {
        // mention sentinel value
        const close = s.indexOf(SENT_R, i + SENT_L.length);
        valueEnd = close === -1 ? i : close + SENT_R.length - 1;
        containsValidMention = true; // by construction, only VALID sentinels exist
        i = valueEnd + 1;
      } else {
        // number/true/false/null/unquoted junk until comma or closing brace
        while (i < s.length && s[i] !== "," && s[i] !== "}") i++;
        valueEnd = i - 1;
        containsValidMention = s.slice(valueStart, valueEnd + 1).includes(SENT_L);
      }

      // lookahead for comma to decide segmentEnd and update lastTopComma
      let j = i; while (j < rootEnd && /\s/.test(s[j])) j++;
      const hasFollowingComma = s[j] === ",";
      const segmentStart = (lastTopComma >= rootStart) ? (lastTopComma + 1) : keyStart;
      const segmentEnd = hasFollowingComma ? (j + 1) : i;

      if (!containsValidMention) {
        // DROP this whole "key: value" segment (including comma spacing)
        // Emit everything before this segment that hasn't been emitted yet.
        result += s.slice(lastEmit, segmentStart);
        lastEmit = segmentEnd;
        // advance i to after the comma (already at i) and update lastTopComma
        if (hasFollowingComma) { lastTopComma = j; i = j + 1; }
        continue;
      }

      // KEEP: just move on; also update lastTopComma if we saw the following comma
      if (hasFollowingComma) lastTopComma = j;
      // nothing emitted yet; proceed
    }

    // Emit the tail (including final '}' etc.)
    result += s.slice(lastEmit);
    return result;
  };

  const prunePayloadJsonByMentionsAndKeys = (payloadJson, validIds) => {
    let nodes = safeParse(payloadJson);
    if (!Array.isArray(nodes)) return payloadJson; // not parseable → keep original

    // 1) serialize nodes to a string with ONLY valid mentions preserved as sentinels
    const s = nodesToStringWithValidMentions(nodes, validIds);

    // 2) remove top-level keys whose values contain NO valid mentions
    const s2 = removeTopLevelKeysWithoutMentions(s);

    // 3) rebuild nodes from s2 (text + mention nodes again)
    const rebuilt = stringToNodesBySentinel(s2);

    try {
      return JSON.stringify(rebuilt);
    } catch {
      return payloadJson;
    }
  };

  // ---------- main ----------
  const validIds = collectValidMentionIdsFromSchema2(schema2);

  // A) prune params (NEW)
  if (Array.isArray(newSchema1.params)) {
    newSchema1.params = pruneParamsByMentions(newSchema1.params, validIds);
  }

  // B) prune payload.json → drop invalid mentions + drop whole top-level keys with no valid mentions (UPDATED)
  if (Array.isArray(newSchema1.payload)) {
    newSchema1.payload = newSchema1.payload.map((block) => {
      if (!block || block.json == null) return block;
      return { ...block, json: prunePayloadJsonByMentionsAndKeys(block.json, validIds) };
    });
  }

  // (Optional) If you also want to hard-prune mentions inside headers similarly, you can
  // apply the same "nodesToStringWithValidMentions → stringToNodesBySentinel" round-trip
  // on header fields. Left untouched because your example didn't require it.

  return newSchema1; // always a new object
}

/* ======================
   Example usage:
   ======================
const updatedSchema1 = updateSchemaWithMentions(schema1, schema2);
console.log(JSON.stringify(updatedSchema1.payload, null, 2));
*/



export default function IntegrationApiConfig({
  llm_data = {},
  handleSaveAlljson,
  settingApiData,
  api_config,
  apiOptions = [],
  ll_data,
  setAlertDetails,
  select_api_type,
  final_resulted_json,
  re_map_ui,
}) {
  // console.log(
  //   "🚀 ~ IntegrationApiConfig ~ final_resulted_json:",
  //   api_config,ll_data
  // );
  let sampleApiData = {
    url: "",
    method: "GET",
    headers: [
      {
        key: "Referer",
        value: "https://genai-solutions-dev.uc.r.appspot.com",
        isDefault: true,
        path: "Referer",
      },
    ],
    params: [],
    payload: [],
    subApis: [],
    position: { x: 50, y: 50 },
    isLooping: false,
    selectedPath: "",
    loopCount: "1",
  };

  const [apiData, setApiData] = useState({});
  // console.log("🚀 ~ apiDataapiDataapiData:", apiData);
  const [api_type, setApi_type] = useState("");

  const [response, setResponse] = useState({});

  const [mergedResponses, setMergedResponses] = useState({
    input_parameters: llm_data || {},
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedApiPath, setSelectedApiPath] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [progress, setProgress] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [input_parameters, setinput_parameters] = useState({});
  // console.log(
  //   "🚀 ~ IntegrationApiConfig ~ input_parameters:",
  //   input_parameters
  // );

  const getSuggestions = (type) => {
    let paths = [];

    switch (type) {
      case "header":
        paths = [
          {
            label: "Content-Type",
            key: "content-type",
            defaultValue: "application/json",
          },
          {
            label: "Authorization",
            key: "authorization",
            defaultValue: "Bearer <token>",
          },
          { label: "Accept", key: "accept", defaultValue: "application/json" },
          {
            label: "Cache-Control",
            key: "cache-control",
            defaultValue: "no-cache",
          },
          {
            label: "User-Agent",
            key: "user-agent",
            defaultValue: "GenAI-Client/1.0",
          },
          { label: "X-Request-ID", key: "x-request-id", defaultValue: "" },
          {
            label: "Cookie",
            key: "cookie",
            defaultValue: "sessionId=abc123; theme=dark",
          },
          {
            label: "Referer",
            key: "referer",
            defaultValue: "https://genai-solutions-dev.uc.r.appspot.com",
          },
        ];
        break;

      // case "params": // Path params
      //   paths = [
      //     { label: "apikey", key: "apikey", defaultValue: "" },
      //     { label: "key", key: "key", defaultValue: "" },

      //   ];
      //   break;

      // case "query": // Query string
      //   paths = [
      //     { label: "Page", key: "page", defaultValue: "1" },
      //     { label: "Limit", key: "limit", defaultValue: "20" },
      //     { label: "Sort By", key: "sortBy", defaultValue: "createdAt" },
      //     { label: "Filter", key: "filter", defaultValue: "active" },
      //     { label: "Search", key: "search", defaultValue: "" },
      //     { label: "Locale", key: "locale", defaultValue: "en-US" },
      //   ];
      //   break;

      // case "payload": // Request body
      //   paths = [
      //     { label: "First Name", key: "firstName", defaultValue: "" },
      //     { label: "Last Name", key: "lastName", defaultValue: "" },
      //     { label: "Email", key: "email", defaultValue: "" },
      //     { label: "Password", key: "password", defaultValue: "" },
      //     { label: "Message", key: "message", defaultValue: "" },
      //     { label: "Status", key: "status", defaultValue: "" },
      //     { label: "Tags", key: "tags", defaultValue: "" },
      //   ];
      //   break;

      default:
        paths = [];
    }

    return paths.map((path) => ({
      label: path.label,
      value: JSON.stringify([
        {
          type: "text",
          text: path.defaultValue ?? "",
        },
      ]),
    }));
  };

  useEffect(() => {
    if (final_resulted_json?.apiResponse || ll_data) {
      const properties = ll_data?.answer?.properties || {};
      let all_llm_data = {};
      all_llm_data = schemaToEndParamsFlat({properties});

      setResponse({
        ...response,
        ...final_resulted_json?.apiResponse,
        input_parameters: {
          ...all_llm_data,
          ...final_resulted_json?.apiResponse?.input_parameters,
        },
      });
    }
  }, []);
  useEffect(() => {
    if (final_resulted_json?.apiResponse || ll_data) {
      const properties = ll_data?.answer?.properties || {};
      let all_llm_data = {};
      all_llm_data = schemaToEndParamsFlat({properties});

      // console.log(
      //   "🚀 ~ IntegrationApiConfig ~ all_llm_data:",
      //   properties,
      //   all_llm_data
      // );

      setinput_parameters({
        // ...response?.input_parameters,
        ...all_llm_data,
        //  ...final_resulted_json?.apiResponse?.input_parameters,
      });
    }
  }, [ll_data]);



   useEffect(() => {
    if (api_config) {
      if (!api_config?.payload?.type) {
        const properties = ll_data?.answer?.properties || {};
        let keys = schemaToEndParamsFlat({properties})
        // console.log("🚀 ~ IntegrationApiConfig ~ keys:", keys)
        let updatedData = updateSchemaWithMentions(api_config,keys)
        console.log("🚀 ~ IntegrationApiConfig ~ updatedDataupdatedDataupdatedData:", updatedData,keys,ll_data,api_config)
        // setApiData(api_config);
        setApiData(updatedData);
      }
    } else {
      setApiData(sampleApiData);
    }
  }, []);

  const updateNested = (path, updater) => {
    setApiData((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      let target = updated;
      for (let i = 0; i < path.length - 1; i++) {
        target[path[i]] = Array.isArray(target[path[i]])
          ? [...target[path[i]]]
          : { ...target[path[i]] };
        target = target[path[i]];
      }
      const lastKey = path[path.length - 1];
      if (Array.isArray(target[lastKey])) {
        target[lastKey] = updater(target[lastKey]);
      } else {
        target[lastKey] = updater(target[lastKey]);
      }
      return updated;
    });
  };

  const handleNodesChange = useCallback(
    (changes) => {
      onNodesChange(changes);
      changes.forEach((change) => {
        if (change.type === "position" && change.position) {
          const nodeId = change.id;
          const node = nodes.find((n) => n.id === nodeId);
          if (node) {
            const path = node.data.path;
            setApiData((prev) => {
              const updated = JSON.parse(JSON.stringify(prev));
              let target = updated;
              if (path.length === 0) {
                target.position = {
                  x: change.position.x,
                  y: change.position.y,
                };
              } else {
                for (let i = 0; i < path.length - 1; i++) {
                  target = target[path[i]];
                }
                const index = path[path.length - 1];
                target[index] = {
                  ...target[index],
                  position: { x: change.position.x, y: change.position.y },
                };
              }
              return updated;
            });
          }
        }
      });
    },
    [nodes, onNodesChange]
  );

  const handleSubApiCall = (selectedApiPath) => {
    const subApi = getValueFromPath(selectedApiPath?.join("."), apiData);
    if (subApi && subApi?.isLooping && subApi.selectedPath) {
      const handleLoopApiCall = async () => {
        setIsLoading(true);
        setProgress("Processing loop API call...");
        let pathData = getValueFromPath(subApi.selectedPath, response);
        let parsedArray;
        try {
          parsedArray =
            typeof pathData === "string" ? JSON.parse(pathData) : pathData;
        } catch (e) {
          setIsLoading(false);
          setProgress("Error in loop API call");
          return;
        }
        if (!Array.isArray(parsedArray)) {
          setIsLoading(false);
          setProgress("Selected path does not point to an array");
          return;
        }
        const maxLoops = parseInt(subApi.loopCount, 10);
        const results = [];
        const apiName =
          selectedApiPath.length === 0
            ? "Main API"
            : `Sub-API ${
                selectedApiPath.filter((k) => !isNaN(k)).join(".") || 1
              }`;
        for (let i = 0; i < Math.min(maxLoops, parsedArray.length); i++) {
          setProgress(
            `Processing Loop ${i + 1}/${Math.min(
              maxLoops,
              parsedArray.length
            )} for ${apiName}`
          );
          const loopContext = { [subApi.selectedPath]: parsedArray[i] };
          const finalUrl = findMentionsLooping(
            subApi.url,
            { ...response, ...loopContext },
            i,
            subApi.selectedPath
          );
          const finalHeaders = convertToKeyValueObject(
            subApi.headers || [],
            loopContext,
            i,
            subApi.selectedPath
          );
          const finalParams = convertToKeyValueObject(
            subApi.params || [],
            loopContext,
            i,
            subApi.selectedPath
          );
          const finalPayload = convertToKeyValueObject(
            subApi.payload || [],
            loopContext,
            i,
            subApi.selectedPath
          );
          let urlWithParams = finalUrl;
          if (subApi.method === "GET" && Object.keys(finalParams).length > 0) {
            const queryString = new URLSearchParams(finalParams).toString();
            urlWithParams = `${finalUrl}${
              finalUrl.includes("?") ? "&" : "?"
            }${queryString}`;
          }
          try {
            const fetchOptions = {
              method: subApi.method || "GET",
              headers: finalHeaders,
            };
            if (
              subApi.method !== "GET" &&
              Object.keys(finalPayload).length > 0
            ) {
              fetchOptions.body = JSON.stringify(finalPayload);
              fetchOptions.headers["Content-Type"] = "application/json";
            }
            const requestData = {
              method: subApi.method || "GET",
              url: urlWithParams,
              headers: finalHeaders,
              params: finalParams || [],
              payload: finalPayload || [],
              responsePath: subApi.responsePath || "",
            };
            const response = await fetch("/api/call", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(requestData),
            });
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            setAlertDetails({
              message: "Api is successfully fetched",
              severity: "info",
              status: true,
              title: "API Fetched - ",
            });
            results.push({ index: i, data, loopContext });
          } catch (err) {
            setAlertDetails({
              message: `Error in Fetching api data from url: ( ${finalUrl} ). Please Try Again`,
              severity: "error",
              status: true,
              title: "API Fetch Error - ",
            });
            results.push({ index: i, error: err.message, loopContext });
          }
        }
        const key = `subapi${
          selectedApiPath.filter((k) => !isNaN(k)).join("_") || 1
        }_loop`;

        setResponse((prev) => ({
          ...prev,
          [key]: results,
        }));
        setIsLoading(false);
        setProgress("Loop API call completed");
      };
      handleLoopApiCall();
    } else {
      handleIndividualApiCall(selectedApiPath);
    }
  };

  const handleIndividualApiCall = async (path, loopResults = null) => {
    setIsLoading(true);
    setProgress("Processing individual API call...");
    if (loopResults) {
      const key = `subapi${path.filter((k) => !isNaN(k)).join("_") || 1}_loop`;
      setResponse((prev) => ({
        ...prev,
        [key]: loopResults,
      }));
      setIsLoading(false);
      setProgress("Individual API call completed");
      return;
    }

    let target = apiData;
    for (const key of path) {
      target = target[key];
    }

    const result = await callSingleApi(target, path, true);
    if (result && !result.error) {
      const key =
        path.length === 0
          ? "main"
          : `subapi${path.filter((k) => !isNaN(k)).join("_") || 1}`;
      setResponse((prev) => ({
        ...prev,
        [key]: result,
      }));
    }
    setIsLoading(false);
    setProgress("Individual API call completed");
  };

 
  // console.log("ll_data?.answer", ll_data?.answer);
  useEffect(() => {
    if (ll_data?.answer?.properties && api_type) {
      const properties = ll_data.answer.properties;
      // input_parameters
      const propertyKeys = Object.keys(input_parameters).map((key) => {
        let value = `[{"type":"mention","attrs":{"id":"input_parameters.${key}","label":"input_parameters.${key}","mentionSuggestionChar":"@"}}]`;
        return {
          key,
          value: value,
          isDefault: false,
          path: key,
        };
      });

      setApiData((prev) => {
        let allPayload = [];
        let allParams = [];
        let prevParams =
          prev?.params && prev?.params?.length ? prev?.params : [];

        // let prevPayload = prev?.payload && prev?.payload?.length ?prev?.payload :[]
        // for(let properKeys of prevParams){
        //   let found = null

        //   if(api_type === "result_api_params"){
        //     found  = propertyKeys?.find(k=>k.key==properKeys?.key)
        //     if(found){
        //       allParams.push(found)
        //     }else{
        //       allParams.push(properKeys)
        //     }
        //   }
        // }
        // for(let properKeys of prevPayload){
        //   let found = null

        //    if(api_type === "result_api_payload"){
        //     found  = propertyKeys?.find(k=>k.key==properKeys?.key)
        //     if(found){
        //       allPayload.push(found)
        //     }else{
        //       allPayload.push(properKeys)
        //     }
        //   }
        // }

        return {
          ...prev,
          params:
            api_type === "result_api_params"
              ? prev?.params && prev?.params?.length
                ? prev?.params
                : propertyKeys
              : prev?.params,
          payload:
            api_type === "result_api_payload"
              ? prev?.payload && prev?.payload?.length
                ? prev?.payload
                : propertyKeys
              : prev?.payload,
          // params: allParams,
          // payload: allPayload
        };
      });
    }
  }, [api_type]);

  function limitArrayElements(input, limit = 4, seen = new WeakSet()) {
    if (input == null) return input;

    if (typeof input === "object" && input !== null) {
      if (seen.has(input)) return null;
      seen.add(input);
    }

    if (Array.isArray(input)) {
      return input
        .slice(0, limit)
        .map((item) => limitArrayElements(item, limit, seen));
    }

    if (typeof input === "object") {
      const result = {};
      for (const key in input) {
        if (Object.prototype.hasOwnProperty.call(input, key)) {
          result[key] = limitArrayElements(input[key], limit, seen);
        }
      }
      return result;
    }

    return input;
  }

  useEffect(() => {
    if (apiData) {
      handleSaveAlljson({ action_type: apiData });
    }
  }, [apiData]);

  useEffect(() => {
    if (response) {
      settingApiData(response);
      handleSaveAlljson({ apiResponse: limitArrayElements(response, 2) });
    }
  }, [response, settingApiData]);

  useEffect(() => {
    if (select_api_type) {
      setApi_type(select_api_type);
    }
  }, [select_api_type]);

  useEffect(() => {
    const generateNodesAndEdges = () => {
      const newNodes = [];
      const newEdges = [];
      let nodeId = 0;

      const NODE_WIDTH = 200;
      const NODE_HEIGHT = 80;
      const HORIZONTAL_SPACING = 300;
      const VERTICAL_SPACING = 100;

      const mainPosition =
        apiData.position &&
        !isNaN(apiData.position.x) &&
        !isNaN(apiData.position.y)
          ? apiData.position
          : { x: 50, y: 50 };
      newNodes.push({
        id: `node-${nodeId}`,
        type: "custom",
        position: mainPosition,
        data: {
          name: "Main API",
          url: apiData.url,
          method: apiData.method,
          onEdit: (e) => handleEditClick(e, []),
          onCopy: (path) => copyParentApi([...path, "subApis"]),
          onTest: (path) => handleSubApiCall(path),
          isData: apiData?.driverPath
            ? getValueFromPath(apiData?.selectedPath, apiData)
            : true,
          path: [],
          onRemove: removeSubApi,
        },
        draggable: true,
      });
      const mainNodeId = `node-${nodeId}`;
      nodeId++;

      const traverseSubApis = (
        subApis,
        parentNodeId,
        path,
        parentPosition,
        depth = 1
      ) => {
        subApis.forEach((subApi, idx) => {
          const currentPath = [...path, idx];
          const nodeIdStr = `node-${nodeId}`;
          const position =
            subApi.position &&
            !isNaN(subApi.position.x) &&
            !isNaN(subApi.position.y)
              ? subApi.position
              : {
                  x: parentPosition.x + HORIZONTAL_SPACING * depth,
                  y: parentPosition.y + VERTICAL_SPACING * (idx + 1),
                };

          newNodes.push({
            id: nodeIdStr,
            type: "custom",
            position,
            data: {
              name: `Sub-API ${
                currentPath.filter((k) => !isNaN(k)).join(".") || idx + 1
              }`,
              url: subApi.url,
              method: subApi.method,
              isLooping: subApi.isLooping,
              onEdit: (e) => handleEditClick(e, currentPath),
              onCopy: (path) => copyParentApi([...path, "subApis"]),
              onTest: (path) => handleSubApiCall(path),
              path: currentPath,
              isData:
                subApi?.selectedPath && subApi?.isLooping
                  ? getValueFromPath(subApi?.selectedPath, response)
                  : true,
              onRemove: removeSubApi,
            },
            draggable: true,
          });
          newEdges.push({
            id: `edge-${parentNodeId}-${nodeIdStr}`,
            source: parentNodeId,
            target: nodeIdStr,
            type: "smoothstep",
            animated: true,
            style: { stroke: "#4d35ff" },
          });
          nodeId++;

          if (subApi.subApis && subApi.subApis.length > 0) {
            traverseSubApis(
              subApi.subApis,
              nodeIdStr,
              [...currentPath, "subApis"],
              position,
              depth + 1
            );
          }
        });
      };

      if (apiData.subApis && apiData.subApis.length > 0) {
        traverseSubApis(apiData.subApis, mainNodeId, ["subApis"], mainPosition);
      }

      setNodes(newNodes);
      setEdges(newEdges);
    };

    generateNodesAndEdges();
  }, [apiData, setNodes, setEdges, response]);

  const onConnect = useCallback(
    (params) => {
      const { source, target } = params;
      const sourceNode = nodes.find((node) => node.id === source);
      const targetNode = nodes.find((node) => node.id === target);

      if (sourceNode && targetNode) {
        if (targetNode.data.path.length === 0) {
          return;
        }

        setApiData((prev) => {
          const updated = JSON.parse(JSON.stringify(prev));
          let targetSubApis = updated;
          const targetPath = targetNode.data.path;

          for (let i = 0; i < targetPath.length - 1; i++) {
            targetSubApis = targetSubApis[targetPath[i]];
          }

          const targetIndex = targetPath[targetPath.length - 1];
          const [movedApi] = targetSubApis.splice(targetIndex, 1);

          const sourcePath = sourceNode.data.path;
          let sourceSubApis = updated;
          for (let i = 0; i < sourcePath.length; i++) {
            sourceSubApis = sourceSubApis[sourcePath[i]];
          }
          if (!sourceSubApis?.subApis) {
            sourceSubApis.subApis = [];
          }
          sourceSubApis.subApis.push(movedApi);

          return updated;
        });

        setEdges((eds) => [
          ...eds,
          {
            id: `edge-${source}-${target}`,
            source,
            target,
            type: "smoothstep",
            animated: true,
            style: { stroke: "#4d35ff" },
          },
        ]);
      }
    },
    [nodes, setEdges, setApiData]
  );

  const addArrayItem = (path, data) => {
    updateNested(path, (arr) => {
      console.log("🚀 ~ addArrayItem ~ arr:", arr);
      let old_data = arr ? arr : [];
      return [
        ...old_data,
        {
          ...(data ? data : { key: "", value: "", isDefault: false, path: "" }),
        },
      ];
    });
  };

  const removeArrayItem = (path, index) => {
    updateNested(path, (arr) => arr.filter((_, i) => i !== index));
  };

  const addSubApi = (path) => {
    let parentPosition = { x: 50, y: 50 };
    if (path.length > 0) {
      let target = apiData;
      for (let i = 0; i < path.length - 1; i++) {
        target = target[path[i]];
      }
      parentPosition =
        target.position &&
        !isNaN(target.position.x) &&
        !isNaN(target.position.y)
          ? target.position
          : {
              x: 50 + (path.length - 1) * 300,
              y: 50 + path[path.length - 1] * 100,
            };
    }
    const newPosition = {
      x: parentPosition.x + 300,
      y: parentPosition.y + 100,
    };

    updateNested(path, (arr) => {
      if (!arr || !arr?.length) {
        return [
          ...[...(arr ? arr : [])],
          {
            url: "",
            method: "GET",
            headers: [],
            params: [],
            payload: [],
            subApis: [],
            responsePath: "",
            isLooping: false,
            selectedPath: "",
            loopCount: "1",
            position: newPosition,
          },
        ];
      } else {
        return [
          ...arr,
          {
            url: "",
            method: "GET",
            headers: [],
            params: [],
            payload: [],
            subApis: [],
            responsePath: "",
            isLooping: false,
            selectedPath: "",
            loopCount: "1",
            position: newPosition,
          },
        ];
      }
    });
  };

  const removeSubApi = (path) => {
    const parentPath = path.slice(0, -1);
    const index = path[path.length - 1];
    updateNested(parentPath, (arr) => arr.filter((_, i) => i !== index));
  };

  const copyParentApi = (path) => {
    setApiData((prev) => {
      let updated = JSON.parse(JSON.stringify(prev));
      let target = updated;
      let parentPath = path.slice(0, -2);
      if (parentPath.length === 0) {
        const copiedMainApi = JSON.parse(JSON.stringify(updated));
        copiedMainApi.subApis = [];
        copiedMainApi.position = {
          x: (updated?.position?.x || 0) + 50,
          y: (updated?.position?.y || 0) + 100,
        };
        updated.subApis.push(copiedMainApi);
        return updated;
      }

      for (let i = 0; i < parentPath.length - 1; i++) {
        target = target[parentPath[i]];
      }
      const lastKey = parentPath[parentPath.length - 1];
      let parentApi = JSON.parse(
        JSON.stringify(target[lastKey][path[path.length - 2]])
      );
      parentApi.position = {
        x: (parentApi?.position?.x || 0) + 50,
        y: (parentApi?.position?.y || 0) + 100,
      };
      parentApi.subApis = [];
      target[lastKey].push(parentApi);
      return updated;
    });
  };

  const updateArrayItem = (path, index, field, value) => {
    updateNested(path, (arr) => {
      const updatedArr = [...arr];
      updatedArr[index] = { ...updatedArr[index], [field]: value };
      return updatedArr;
    });
  };

  const updateSubApiField = (path, field, value) => {
    updateNested(path, (obj) => ({ ...obj, [field]: value }));
  };

  const callSingleApi = async (apiNode, path = [], shouldFetch = true) => {
    const apiName =
      path.length === 0
        ? "Main API"
        : `Sub-API ${path.filter((k) => !isNaN(k)).join(".")}`;
    setProgress(`Processing ${apiName}`);
    if (!apiNode.url) return null;

    const result = { response: null };
    const finalHeaders = convertToKeyValueObject(
      apiNode.headers || [],
      {},
      0,
      ""
    );
    const finalParams = convertToKeyValueObject(
      apiNode.params || [],
      {},
      0,
      ""
    );
    const finalPayload = convertToKeyValueObject(
      apiNode.payload || [],
      {},
      0,
      ""
    );
    let finalUrl = findMentions(apiNode.url, mergedResponses || {});

    if (apiNode.method === "GET" && Object.keys(finalParams).length > 0) {
      const queryString = new URLSearchParams(finalParams).toString();
      finalUrl = `${finalUrl}${
        finalUrl.includes("?") ? "&" : "?"
      }${queryString}`;
    }

    if (!shouldFetch) {
      return {
        method: apiNode.method || "GET",
        url: finalUrl,
        headers: finalHeaders,
        params: finalParams,
        payload: finalPayload,
        responsePath: apiNode.responsePath || "",
      };
    }

    try {
      const fetchOptions = {
        method: apiNode.method || "GET",
        headers: finalHeaders,
      };

      if (apiNode.method !== "GET" && Object.keys(finalPayload).length > 0) {
        fetchOptions.body = JSON.stringify(finalPayload);
        fetchOptions.headers["Content-Type"] = "application/json";
      }

      const requestData = {
        method: apiNode.method || "GET",
        url: finalUrl,
        headers: finalHeaders,
        params: finalParams || [],
        payload: finalPayload || [],
        responsePath: apiNode.responsePath || "",
      };

      const response = await fetch("/api/call", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      let alertDetailss = {};
      alertDetailss.message = "Api is successfully fetched";
      alertDetailss.severity = "info";
      alertDetailss.status = true;
      alertDetailss.title = "API Fetched - ";
      setAlertDetails(alertDetailss);
      return data;
    } catch (err) {
      result.error = err.message;
      let alertDetailss = {};
      alertDetailss.message = `Error in Fetching api data from url: ( ${finalUrl} ). Please Try Again`;
      alertDetailss.severity = "error";
      alertDetailss.status = true;
      alertDetailss.title = "API Fetch Error - ";
      setAlertDetails(alertDetailss);
      return result;
    }
  };

  const recursiveApiCall = async (
    apiNode,
    path = [],
    subApiIndex = 0,
    shouldFetch = true
  ) => {
    const apiName =
      path.length === 0
        ? "Main API"
        : `Sub-API ${
            path.filter((k) => !isNaN(k)).join("_") || subApiIndex + 1
          }`;
    setProgress(`Processing ${apiName}`);
    const result = await callSingleApi(apiNode, path, shouldFetch);
    const allResponses = {};

    if (result && !result.error) {
      const key =
        path.length === 0
          ? "main"
          : `subapi${
              path.filter((k) => !isNaN(k)).join("_") || subApiIndex + 1
            }`;
      allResponses[key] = result;
      result.subApis = [];
      for (let [index, subApi] of (apiNode.subApis || []).entries()) {
        const subResult = await recursiveApiCall(
          subApi,
          [...path, "subApis", index],
          index,
          shouldFetch
        );
        if (subResult) {
          result.subApis.push(subResult);
          Object.assign(allResponses, subResult.allResponses);
        }
      }
    } else if (result && result.error) {
      allResponses.error = result.error;
    }

    return { ...result, allResponses };
  };

  const handleApiCall = async () => {
    setIsLoading(true);
    setProgress("Starting API calls...");
    const result = await recursiveApiCall(apiData, [], 0, true);
    setResponse(result);
    setMergedResponses({
      input_parameters: llm_data || {},
      ...result?.allResponses,
    });
    setIsLoading(false);
    setProgress("API calls completed");
  };

  const handleCallAllApi = async (shouldFetch = false) => {
    setIsLoading(true);
    setProgress("Starting all API calls...",apiData);
    console.log("🚀 ~ handleCallAllApi ~ apiData:", apiData)

    const result = await callAllApi(
      apiData,
      response,
      setMergedResponses,
      response?.input_parameters,
      setProgress,
      setIsLoading
    );

    if (shouldFetch) {
      setResponse(result);
      re_map_ui?.(limitArrayElements(result));
    }
    setIsLoading(false);
    setProgress("All API calls completed");
    return result;
  };

  function convertToKeyValueObject(
    array,
    loopContext = {},
    index,
    selectedPath
  ) {
    const result = {};
    array.forEach((item) => {
      const keyObj = findMentionsLooping(
        item.key,
        { ...mergedResponses, ...loopContext },
        index,
        selectedPath
      );
      
      const valueObj = findMentionsLooping(
        item.value,
        { ...mergedResponses, ...loopContext },
        index,
        selectedPath
      );

      console.log("🚀 ~ convertToKeyValueObject ~ keyObj:", keyObj,valueObj)
      result[keyObj] = valueObj;
    });
    return result;
  }

  const handleEditClick = (event, path) => {
    setAnchorEl(event.currentTarget);
    setSelectedApiPath(path);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setSelectedApiPath(null);
  };

  const handleAddNode = () => {
    addSubApi(["subApis"]);
  };

  const handleAddChipToArray = (path, field, value) => {
    updateNested([...path, field], (arr) => [
      ...arr,
      { key: "", value: value, isDefault: false, path: "" },
    ]);
  };

  const open = Boolean(anchorEl);

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "Arial",
        maxWidth: "1200px",
        color: "white",
        display: "flex",
        gap: "20px",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          width: "100%",
          height: "500px",
          background: "#1A1C37",
          borderRadius: "8px",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          style={{ background: "#1A1C37" }}
        >
          <Controls style={{ color: "black" }} />
          <Background color="#4d35ff" gap={16} />
        </ReactFlow>
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "end",
          }}
        >
          <SuperAppButton
            type="button"
            onClick={handleAddNode}
            style={{ padding: "5px 10px", backgroundColor: "#4d35ff" }}
          >
            Add Node
          </SuperAppButton>

          {response && (
            <div style={{ marginBlock: 10 }}>
              <Typography variant="h6">ALL API Result:</Typography>
              <pre
                style={{
                  background: "transparent",
                  width: "300px",
                  padding: "0",
                  maxHeight: "400px",
                  overflow: "auto",
                  borderRadius: 10,
                }}
              >
                <JsonViewer jsonData={response} />
              </pre>
            </div>
          )}
        </div>
      </div>

      <>
        <Typography variant="h5" style={{ marginTop: "20px" }}>
          Edit your Input Parameters values:
        </Typography>
        <pre
          style={{
            background: "#f9f9f9",
            padding: "0",
            maxHeight: "300px",
            overflow: "auto",
            color: "#333",
          }}
        >
          <JsonViewer
            jsonData={input_parameters}
            onChange={(data) => {
              setResponse({ ...response, input_parameters: data });
            }}
            editable={false}
          />
        </pre>
      </>
      <Box display="flex" alignItems="center" gap={2}>
        {!isLoading && (
          <SuperAppButton
            type="button"
            onClick={() => handleCallAllApi(true)}
            style={{ padding: "5px 10px" }}
          >
            Trigger All API Calls
          </SuperAppButton>
        )}
        <Box flex={1} maxWidth="400px">
          {isLoading && <LinearProgress color="primary" />}
          {progress && (
            <Typography variant="body2" color="white" mt={1}>
              {progress}
            </Typography>
          )}
        </Box>
      </Box>
      <>
        <Typography variant="h5" style={{ marginTop: "20px" }}>
          Full API JSON:
        </Typography>
        <pre
          style={{
            background: "#f9f9f9",
            padding: "0",
            maxHeight: "300px",
            overflow: "auto",
            color: "#333",
          }}
        >
          <JsonViewer jsonData={apiData} />
        </pre>
      </>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        PaperProps={{
          style: {
            background: "#2A2C4B",
            color: "white",
            width: "auto",
            minWidth: "500px",
            borderRadius: "8px",
            height: "600px",
            overflow: "auto",
          },
        }}
      >
        {selectedApiPath && (
          <>
            <div
              style={{
                position: "sticky",
                top: 0,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "0",
                background: "#141a53",
                zIndex: 10000,
                padding: 10,
              }}
            >
              <Typography variant="h6">
                Edit{" "}
                {selectedApiPath?.length === 0
                  ? "Main API"
                  : `Sub-API ${
                      selectedApiPath.filter((k) => !isNaN(k)).join(".") || 1
                    }`}
              </Typography>
              <IconButton
                onClick={handlePopoverClose}
                style={{ color: "white" }}
              >
                <IoMdClose size={20} />
              </IconButton>
            </div>
            <div style={{ padding: 20 }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                }}
              >
                {!selectedApiPath?.length && (
                  <div style={{ margin: "10px 0", width: "100%" }}>
                    <label htmlFor="api-mapping" style={{ color: "white" }}>
                      Choose How You Want to Send Data with the API
                    </label>
                    <select
                      id="api-mapping"
                      value={api_type || ""}
                      onChange={(e) => setApi_type(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px",
                        color: "white",
                        background: "transparent",
                        border: "1px solid #ccc",
                      }}
                    >
                      <option style={{ color: "black" }} value="">
                        Select your Mapping
                      </option>
                      {apiOptions.map((option) => (
                        <option
                          style={{ color: "black" }}
                          key={option.id}
                          value={option.id}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <Typography style={{ minWidth: "80px" }}>Method:</Typography>
                  <TextField
                    select
                    value={
                      selectedApiPath?.length === 0
                        ? apiData.method
                        : getValueFromPath(selectedApiPath.join("."), apiData)
                            ?.method || "GET"
                    }
                    onChange={(e) => {
                      if (selectedApiPath?.length === 0) {
                        setApiData({ ...apiData, method: e.target.value });
                      } else {
                        updateSubApiField(
                          selectedApiPath,
                          "method",
                          e.target.value
                        );
                      }
                    }}
                    SelectProps={{ native: true }}
                    style={{ width: "100px" }}
                    variant="outlined"
                    size="small"
                  >
                    <option style={{ color: "black" }} value="GET">
                      GET
                    </option>
                    <option style={{ color: "black" }} value="POST">
                      POST
                    </option>
                    <option style={{ color: "black" }} value="PUT">
                      PUT
                    </option>
                    <option style={{ color: "black" }} value="DELETE">
                      DELETE
                    </option>
                  </TextField>
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <Typography style={{ minWidth: "80px" }}>URL:</Typography>
                  <div style={{ flex: 1 }}>
                    <CustomTextEditor
                      prompt={
                        selectedApiPath?.length === 0
                          ? apiData.url
                          : getValueFromPath(selectedApiPath.join("."), apiData)
                              ?.url || ""
                      }
                      handleTitleChange={(e) => {
                        if (selectedApiPath?.length === 0) {
                          setApiData({ ...apiData, url: e.target.value });
                        } else {
                          updateSubApiField(
                            selectedApiPath,
                            "url",
                            e.target.value
                          );
                        }
                      }}
                      style={{ flex: 1 }}
                      sampleData={response}
                    />
                  </div>
                </div>
                {selectedApiPath?.length > 0 && (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <label
                        style={{
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            getValueFromPath(selectedApiPath.join("."), apiData)
                              ?.isLooping || false
                          }
                          onChange={(e) =>
                            updateSubApiField(
                              selectedApiPath,
                              "isLooping",
                              e.target.checked
                            )
                          }
                          style={{ accentColor: "white" }}
                        />
                        Looping
                      </label>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <Typography style={{ minWidth: "80px" }}>
                        Path:
                      </Typography>
                      <Autocomplete
                        options={getArrayPaths(response)}
                        getOptionLabel={(option) => option.label}
                        value={
                          getArrayPaths(response).find(
                            (p) =>
                              p.id ===
                              getValueFromPath(
                                selectedApiPath.join("."),
                                apiData
                              )?.selectedPath
                          ) || null
                        }
                        onChange={(e, newValue) =>
                          updateSubApiField(
                            selectedApiPath,
                            "selectedPath",
                            newValue ? newValue.id : ""
                          )
                        }
                        disabled={
                          !getValueFromPath(selectedApiPath.join("."), apiData)
                            ?.isLooping
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            placeholder="Select Path"
                          />
                        )}
                        renderOption={(props, option) => (
                          <Tooltip
                            title={`Path Value: ${JSON.stringify(
                              getValueFromPath(option.label, response),
                              null,
                              2
                            ).slice(0, 400)}...`}
                            placement="right"
                            sx={{ background: "white" }}
                            arrow
                          >
                            <li {...props}>{option.label}</li>
                          </Tooltip>
                        )}
                        style={{ minWidth: "200px", overflow: "auto" }}
                      />
                      <CustomToolTip
                        text="Click the Trigger All api button to see the options. Select a path from the API response to loop over. This should point to an array in the response data, enabling the API to be called for each item in the array."
                        direction="top"
                      />
                      {getValueFromPath(selectedApiPath.join("."), apiData)
                        ?.selectedPath && (
                        <Typography
                          style={{ marginLeft: "10px", color: "white" }}
                        >
                          Selected Path:{" "}
                          <span>
                            {
                              getValueFromPath(
                                selectedApiPath.join("."),
                                apiData
                              )?.selectedPath
                            }
                          </span>
                        </Typography>
                      )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <Typography style={{ minWidth: "80px" }}>
                        Loop Count:
                      </Typography>
                      <TextField
                        select
                        value={
                          getValueFromPath(selectedApiPath.join("."), apiData)
                            ?.loopCount || "1"
                        }
                        onChange={(e) =>
                          updateSubApiField(
                            selectedApiPath,
                            "loopCount",
                            e.target.value
                          )
                        }
                        disabled={
                          !getValueFromPath(selectedApiPath.join("."), apiData)
                            ?.isLooping
                        }
                        SelectProps={{ native: true }}
                        style={{ width: "100px" }}
                        variant="outlined"
                        size="small"
                      >
                        {[1, 2, 3, 4, 5, 10, 20].map((num) => (
                          <option
                            style={{ width: "100px", color: "black" }}
                            key={num}
                            value={num}
                          >
                            {num}
                          </option>
                        ))}
                      </TextField>
                    </div>
                  </>
                )}
                <KeyValueEditor
                  setAlertDetails={setAlertDetails}
                  pathPrefix={
                    selectedApiPath?.length === 0 ? [] : selectedApiPath
                  }
                  headers={
                    selectedApiPath?.length === 0
                      ? apiData.headers
                      : getValueFromPath(selectedApiPath.join("."), apiData)
                          ?.headers || []
                  }
                  params={
                    selectedApiPath?.length === 0
                      ? apiData.params
                      : getValueFromPath(selectedApiPath.join("."), apiData)
                          ?.params || []
                  }
                  payload={
                    selectedApiPath?.length === 0
                      ? apiData.payload
                      : getValueFromPath(selectedApiPath.join("."), apiData)
                          ?.payload || []
                  }
                  onUpdateArrayItem={updateArrayItem}
                  onAddArrayItem={addArrayItem}
                  onRemoveArrayItem={removeArrayItem}
                  response={response}
                  suggestions={getSuggestions}
                  onAddChipToArray={handleAddChipToArray}
                />
                {/* {selectedApiPath?.length > 0 && (
                  <div style={{ display: "flex", gap: "10px" }}>
                    <SuperAppButton
                      type="button"
                      onClick={() => {
                        const subApi = getValueFromPath(selectedApiPath.join('.'), apiData);
                        if (subApi.isLooping && subApi.selectedPath) {
                          const handleLoopApiCall = async () => {
                            setIsLoading(true);
                            setProgress('Processing loop API call...');
                            let pathData = getValueFromPath(subApi.selectedPath, response);
                            let parsedArray;
                            try {
                              parsedArray = typeof pathData === "string" ? JSON.parse(pathData) : pathData;
                            } catch (e) {
                              setIsLoading(false);
                              setProgress('Error in loop API call');
                              return;
                            }
                            if (!Array.isArray(parsedArray)) {
                              setIsLoading(false);
                              setProgress('Selected path does not point to an array');
                              return;
                            }
                            const maxLoops = parseInt(subApi.loopCount, 10);
                            const results = [];
                            const apiName = selectedApiPath.length === 0 ? 'Main API' : `Sub-API ${selectedApiPath.filter(k => !isNaN(k)).join('.') || 1}`;
                            for (let i = 0; i < Math.min(maxLoops, parsedArray.length); i++) {
                              setProgress(`Processing Loop ${i + 1}/${Math.min(maxLoops, parsedArray.length)} for ${apiName}`);
                              const loopContext = { [subApi.selectedPath]: parsedArray[i] };
                              const finalUrl = findMentionsLooping(subApi.url, { ...response, ...loopContext }, i, subApi.selectedPath);
                              const finalHeaders = convertToKeyValueObject(subApi.headers || [], loopContext, i, subApi.selectedPath);
                              const finalParams = convertToKeyValueObject(subApi.params || [], loopContext, i, subApi.selectedPath);
                              const finalPayload = convertToKeyValueObject(subApi.payload || [], loopContext, i, subApi.selectedPath);
                              let urlWithParams = finalUrl;
                              if (subApi.method === 'GET' && Object.keys(finalParams).length > 0) {
                                const queryString = new URLSearchParams(finalParams).toString();
                                urlWithParams = `${finalUrl}${finalUrl.includes('?') ? '&' : '?'}${queryString}`;
                              }
                              try {
                                const fetchOptions = {
                                  method: subApi.method || "GET",
                                  headers: finalHeaders,
                                };
                                if (subApi.method !== 'GET' && Object.keys(finalPayload).length > 0) {
                                  fetchOptions.body = JSON.stringify(finalPayload);
                                  fetchOptions.headers['Content-Type'] = 'application/json';
                                }
                                const requestData = {
                                  method: subApi.method || 'GET',
                                  url: urlWithParams,
                                  headers: finalHeaders,
                                  params: finalParams || [],
                                  payload: finalPayload || [],
                                  responsePath: subApi.responsePath || ''
                                };
                                const response = await fetch('/api/call', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify(requestData),
                                });
                                if (!response.ok) {
                                  throw new Error(`HTTP error! status: ${response.status}`);
                                }
                                const data = await response.json();
                                setAlertDetails({
                                  message: "Api is successfully fetched",
                                  severity: "info",
                                  status: true,
                                  title: "API Fetched - "
                                });
                                results.push({ index: i, data, loopContext });
                              } catch (err) {
                                setAlertDetails({
                                  message: `Error in Fetching api data from url: ( ${finalUrl} ). Please Try Again`,
                                  severity: "error",
                                  status: true,
                                  title: "API Fetch Error - "
                                });
                                results.push({ index: i, error: err.message, loopContext });
                              }
                            }
                            const key = `subapi${selectedApiPath.filter(k => !isNaN(k)).join('_') || 1}_loop`;
                            setMergedResponses(prev => ({
                              ...prev,
                              llm_data: llm_data || {},
                              [key]: results
                            }));
                            setIsLoading(false);
                            setProgress('Loop API call completed');
                          };
                          handleLoopApiCall();
                        } else {
                          handleIndividualApiCall(selectedApiPath);
                        }
                      }}
                      style={{ margin: "10px 0", padding: "5px 10px" }}
                    >
                      Test API
                    </SuperAppButton>
                  </div>
                )} */}
              </div>
            </div>
          </>
        )}
      </Popover>

      {/* <EditableJson /> */}
    </div>
  );
}

function EditableJson() {
  const [data, setData] = useState({
    productSorting: {
      sort: { data: "PRICE", isdefault: true },
      order: { data: "DESCENDING", isdefault: false },
    },
  });
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [newType, setNewType] = useState("string");
  const [activeAddPath, setActiveAddPath] = useState(null);
  const [editKey, setEditKey] = useState({});
  const [collapsed, setCollapsed] = useState({});

  const parseValue = (value, type, existingIsDefault = false) => {
    try {
      switch (type) {
        case "number":
          const num = Number(value);
          return {
            data: isNaN(num) ? value : num,
            isdefault: existingIsDefault,
          };
        case "boolean":
          if (typeof value === "string") {
            const lower = value.toLowerCase();
            return {
              data: lower === "true" ? true : lower === "false" ? false : value,
              isdefault: existingIsDefault,
            };
          }
          return { data: Boolean(value), isdefault: existingIsDefault };
        case "object":
          if (typeof value === "string" && value.trim()) {
            try {
              return { data: JSON.parse(value), isdefault: existingIsDefault };
            } catch {
              return { data: {}, isdefault: existingIsDefault };
            }
          }
          return {
            data:
              typeof value === "object" &&
              value !== null &&
              !Array.isArray(value)
                ? value
                : {},
            isdefault: existingIsDefault,
          };
        case "array":
          if (typeof value === "string" && value.trim()) {
            try {
              const parsed = JSON.parse(value);
              return {
                data: Array.isArray(parsed) ? parsed : [],
                isdefault: existingIsDefault,
              };
            } catch {
              return { data: [], isdefault: existingIsDefault };
            }
          }
          return {
            data: Array.isArray(value) ? value : [],
            isdefault: existingIsDefault,
          };
        case "string":
        default:
          return { data: String(value), isdefault: existingIsDefault };
      }
    } catch {
      return { data: value, isdefault: existingIsDefault };
    }
  };

  const handleChange = (keyPath, newValue, newType) => {
    const updatedData = JSON.parse(JSON.stringify(data));
    const keys = keyPath.split(".");
    let current = updatedData;
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]].data || current[keys[i]];
    }
    const existingIsDefault =
      current[keys[keys.length - 1]]?.isdefault || false;
    current[keys[keys.length - 1]] = parseValue(
      newValue,
      newType,
      existingIsDefault
    );
    setData(updatedData);
  };

  const handleArrayChange = (keyPath, index, newValue, newType) => {
    const updatedData = JSON.parse(JSON.stringify(data));
    const keys = keyPath.split(".");
    let current = updatedData;
    for (let i = 0; i < keys.length; i++) {
      current = current[keys[i]].data || current[keys[i]];
    }
    const existingIsDefault = current[index]?.isdefault || false;
    current[index] = parseValue(newValue, newType, existingIsDefault);
    setData(updatedData);
  };

  const handleDefaultChange = (keyPath, checked) => {
    const updatedData = JSON.parse(JSON.stringify(data));
    const keys = keyPath.split(".");
    let current = updatedData;
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]].data || current[keys[i]];
    }
    current[keys[keys.length - 1]].isdefault = checked;
    setData(updatedData);
  };

  const handleArrayDefaultChange = (keyPath, index, checked) => {
    const updatedData = JSON.parse(JSON.stringify(data));
    const keys = keyPath.split(".");
    let current = updatedData;
    for (let i = 0; i < keys.length; i++) {
      current = current[keys[i]].data || current[keys[i]];
    }
    current[index].isdefault = checked;
    setData(updatedData);
  };

  const handleKeyChange = (keyPath, oldKey, newKey) => {
    if (!newKey || newKey === oldKey) return;
    const updatedData = JSON.parse(JSON.stringify(data));
    const keys = keyPath.split(".");
    let current = updatedData;
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]].data || current[keys[i]];
    }
    const value = current[oldKey];
    delete current[oldKey];
    current[newKey] = value;
    setData(updatedData);
    setEditKey((prev) => ({ ...prev, [keyPath]: "" }));
  };

  const handleDelete = (keyPath) => {
    const updatedData = JSON.parse(JSON.stringify(data));
    const keys = keyPath.split(".");
    let current = updatedData;
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]].data || current[keys[i]];
    }
    delete current[keys[keys.length - 1]];
    setData(updatedData);
  };

  const handleArrayDelete = (keyPath, index) => {
    const updatedData = JSON.parse(JSON.stringify(data));
    const keys = keyPath.split(".");
    let current = updatedData;
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]].data || current[keys[i]];
    }
    const targetIndex = parseInt(keys[keys.length - 1]);
    if (Array.isArray(current)) {
      current.splice(targetIndex, 1);
    } else {
      // Handle nested array case
      current =
        current[keys[keys.length - 2]].data || current[keys[keys.length - 2]];
      if (Array.isArray(current)) {
        current.splice(targetIndex, 1);
      }
    }
    setData(updatedData);
  };

  const handleAddKey = (parentPath) => {
    if (!newKey || newValue === "") return;
    const updatedData = JSON.parse(JSON.stringify(data));
    const keys = parentPath ? parentPath.split(".") : [];
    let current = updatedData;
    for (let k of keys) {
      current = current[k].data || current[k];
    }
    current[newKey] = parseValue(newValue, newType);
    setData(updatedData);
    setNewKey("");
    setNewValue("");
    setNewType("string");
    setActiveAddPath(null);
  };

  const handleAddArrayElement = (parentPath) => {
    if (newValue === "") return;
    const updatedData = JSON.parse(JSON.stringify(data));
    const keys = parentPath ? parentPath.split(".") : [];
    let current = updatedData;
    for (let k of keys) {
      current = current[k].data || current[k];
    }
    // Ensure current is an array or initialize it as an array
    if (!Array.isArray(current)) {
      current = [];
      if (keys.length > 0) {
        let parent = updatedData;
        for (let i = 0; i < keys.length - 1; i++) {
          parent = parent[keys[i]].data || parent[keys[i]];
        }
        parent[keys[keys.length - 1]] = { data: current, isdefault: false };
      } else {
        updatedData[keys[0]] = { data: current, isdefault: false };
      }
    }
    current.push(parseValue(newValue, newType));
    setData(updatedData);
    setNewValue("");
    setNewType("string");
    setActiveAddPath(null);
  };

  const toggleAddForm = (path, isArray = false) => {
    setActiveAddPath(activeAddPath === path ? null : path);
    setNewKey("");
    setNewValue("");
    setNewType("string");
  };

  const toggleCollapse = (path) => {
    setCollapsed((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const getValueType = (value) => {
    if (value === null) return "string";
    if (Array.isArray(value.data)) return "array";
    if (typeof value.data === "object" && value.data !== null) return "object";
    if (typeof value.data === "number") return "number";
    if (typeof value.data === "boolean") return "boolean";
    return "string";
  };

  const renderValueDisplay = (value) => {
    if (
      (typeof value.data === "object" && value.data !== null) ||
      Array.isArray(value.data)
    ) {
      return JSON.stringify(value.data, null, 2);
    }
    return String(value.data);
  };

  const renderObject = (obj, parentPath = "") => {
    return (
      <div style={{ paddingLeft: "20px", color: "#d1d5db" }}>
        {Array.isArray(obj)
          ? obj.map((value, index) => {
              const fullPath = parentPath
                ? `${parentPath}.${index}`
                : `${index}`;
              const valueType = getValueType(value);
              const isCollapsed = collapsed[fullPath] || false;

              return (
                <div key={fullPath} style={{ marginBottom: "8px" }}>
                  <div
                    style={{ cursor: "pointer", color: "#9ca3af" }}
                    onClick={() => toggleCollapse(fullPath)}
                  >
                    {isCollapsed ? "▶" : "▼"} [{index}]
                  </div>
                  {!isCollapsed && (
                    <>
                      {typeof value.data === "object" && value.data !== null ? (
                        <div>
                          {renderObject(value.data, fullPath)}
                          <button
                            type="button"
                            onClick={() => toggleAddForm(fullPath, true)}
                            style={{
                              padding: "4px 8px",
                              backgroundColor: "#4b5563",
                              color: "white",
                              border: "none",
                              cursor: "pointer",
                            }}
                          >
                            ➕ Add Element
                          </button>
                          {activeAddPath === fullPath && (
                            <div style={{ marginTop: "8px" }}>
                              <input
                                value={newValue}
                                onChange={(e) => setNewValue(e.target.value)}
                                placeholder="Enter value"
                                style={{ padding: "4px", marginRight: "8px" }}
                              />
                              <select
                                value={newType}
                                onChange={(e) => setNewType(e.target.value)}
                                style={{ padding: "4px", marginRight: "8px" }}
                              >
                                <option value="string">String</option>
                                <option value="number">Number</option>
                                <option value="boolean">Boolean</option>
                                <option value="object">Object</option>
                                <option value="array">Array</option>
                              </select>
                              <button
                                type="button"
                                onClick={() => handleAddArrayElement(fullPath)}
                                disabled={newValue === ""}
                                style={{
                                  padding: "4px 8px",
                                  backgroundColor:
                                    newValue === "" ? "#6b7280" : "#10b981",
                                  color: "white",
                                  border: "none",
                                  cursor:
                                    newValue === "" ? "not-allowed" : "pointer",
                                }}
                              >
                                Add
                              </button>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => handleArrayDelete(fullPath, index)}
                            style={{
                              padding: "4px 8px",
                              backgroundColor: "#ef4444",
                              color: "white",
                              border: "none",
                              cursor: "pointer",
                              marginTop: "4px",
                            }}
                          >
                            ❌ Delete
                          </button>
                        </div>
                      ) : (
                        <div style={{ marginLeft: "20px" }}>
                          <input
                            value={renderValueDisplay(value)}
                            onChange={(e) =>
                              handleArrayChange(
                                fullPath,
                                index,
                                e.target.value,
                                valueType
                              )
                            }
                            style={{ padding: "4px", marginRight: "8px" }}
                          />
                          <select
                            value={valueType}
                            onChange={(e) =>
                              handleArrayChange(
                                fullPath,
                                index,
                                renderValueDisplay(value),
                                e.target.value
                              )
                            }
                            style={{ padding: "4px", marginRight: "8px" }}
                          >
                            <option value="string">String</option>
                            <option value="number">Number</option>
                            <option value="boolean">Boolean</option>
                            <option value="object">Object</option>
                            <option value="array">Array</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => handleArrayDelete(fullPath, index)}
                            style={{
                              padding: "4px 8px",
                              backgroundColor: "#ef4444",
                              color: "white",
                              border: "none",
                              cursor: "pointer",
                            }}
                          >
                            ❌ Delete
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })
          : Object.entries(obj).map(([key, value]) => {
              const fullPath = parentPath ? `${parentPath}.${key}` : key;
              const valueType = getValueType(value);
              const isCollapsed = collapsed[fullPath] || false;

              return (
                <div key={fullPath} style={{ marginBottom: "8px" }}>
                  <div
                    style={{ cursor: "pointer", color: "#9ca3af" }}
                    onClick={() => toggleCollapse(fullPath)}
                  >
                    {isCollapsed ? "▶" : "▼"} {key}
                  </div>
                  {!isCollapsed && (
                    <>
                      {typeof value.data === "object" && value.data !== null ? (
                        <div>
                          {renderObject(value.data, fullPath)}
                          <button
                            type="button"
                            onClick={() =>
                              toggleAddForm(fullPath, Array.isArray(value.data))
                            }
                            style={{
                              padding: "4px 8px",
                              backgroundColor: "#4b5563",
                              color: "white",
                              border: "none",
                              cursor: "pointer",
                            }}
                          >
                            ➕ Add{" "}
                            {Array.isArray(value.data) ? "Element" : "Key"}
                          </button>
                          {activeAddPath === fullPath && (
                            <div style={{ marginTop: "8px" }}>
                              {Array.isArray(value.data) ? (
                                <>
                                  <input
                                    value={newValue}
                                    onChange={(e) =>
                                      setNewValue(e.target.value)
                                    }
                                    placeholder="Enter value"
                                    style={{
                                      padding: "4px",
                                      marginRight: "8px",
                                    }}
                                  />
                                  <select
                                    value={newType}
                                    onChange={(e) => setNewType(e.target.value)}
                                    style={{
                                      padding: "4px",
                                      marginRight: "8px",
                                    }}
                                  >
                                    <option value="string">String</option>
                                    <option value="number">Number</option>
                                    <option value="boolean">Boolean</option>
                                    <option value="object">Object</option>
                                    <option value="array">Array</option>
                                  </select>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleAddArrayElement(fullPath)
                                    }
                                    disabled={newValue === ""}
                                    style={{
                                      padding: "4px 8px",
                                      backgroundColor:
                                        newValue === "" ? "#6b7280" : "#10b981",
                                      color: "white",
                                      border: "none",
                                      cursor:
                                        newValue === ""
                                          ? "not-allowed"
                                          : "pointer",
                                    }}
                                  >
                                    Add
                                  </button>
                                </>
                              ) : (
                                <>
                                  <input
                                    value={newKey}
                                    onChange={(e) => setNewKey(e.target.value)}
                                    placeholder="Enter key name"
                                    style={{
                                      padding: "4px",
                                      marginRight: "8px",
                                    }}
                                  />
                                  <input
                                    value={newValue}
                                    onChange={(e) =>
                                      setNewValue(e.target.value)
                                    }
                                    placeholder="Enter value"
                                    style={{
                                      padding: "4px",
                                      marginRight: "8px",
                                    }}
                                  />
                                  <select
                                    value={newType}
                                    onChange={(e) => setNewType(e.target.value)}
                                    style={{
                                      padding: "4px",
                                      marginRight: "8px",
                                    }}
                                  >
                                    <option value="string">String</option>
                                    <option value="number">Number</option>
                                    <option value="boolean">Boolean</option>
                                    <option value="object">Object</option>
                                    <option value="array">Array</option>
                                  </select>
                                  <button
                                    type="button"
                                    onClick={() => handleAddKey(fullPath)}
                                    disabled={!newKey || newValue === ""}
                                    style={{
                                      padding: "4px 8px",
                                      backgroundColor:
                                        !newKey || newValue === ""
                                          ? "#6b7280"
                                          : "#10b981",
                                      color: "white",
                                      border: "none",
                                      cursor:
                                        !newKey || newValue === ""
                                          ? "not-allowed"
                                          : "pointer",
                                    }}
                                  >
                                    Add
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => handleDelete(fullPath)}
                            style={{
                              padding: "4px 8px",
                              backgroundColor: "#ef4444",
                              color: "white",
                              border: "none",
                              cursor: "pointer",
                              marginTop: "4px",
                            }}
                          >
                            ❌ Delete
                          </button>
                        </div>
                      ) : (
                        <div style={{ marginLeft: "20px", color: "#d1d5db" }}>
                          <span style={{ color: "#93c5fd" }}>{key}: </span>
                          <input
                            value={renderValueDisplay(value)}
                            onChange={(e) =>
                              handleChange(fullPath, e.target.value, valueType)
                            }
                            style={{
                              padding: "4px",
                              marginRight: "8px",
                              backgroundColor: "#1f2937",
                              color: "#d1d5db",
                              border: "1px solid #4b5563",
                            }}
                          />
                          <select
                            value={valueType}
                            onChange={(e) =>
                              handleChange(
                                fullPath,
                                renderValueDisplay(value),
                                e.target.value
                              )
                            }
                            style={{
                              padding: "4px",
                              marginRight: "8px",
                              backgroundColor: "#1f2937",
                              color: "#d1d5db",
                              border: "1px solid #4b5563",
                            }}
                          >
                            <option value="string">String</option>
                            <option value="number">Number</option>
                            <option value="boolean">Boolean</option>
                            <option value="object">Object</option>
                            <option value="array">Array</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => handleDelete(fullPath)}
                            style={{
                              padding: "4px 8px",
                              backgroundColor: "#ef4444",
                              color: "white",
                              border: "none",
                              cursor: "pointer",
                            }}
                          >
                            ❌ Delete
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
        {!parentPath && (
          <div style={{ marginTop: "8px" }}>
            <button
              type="button"
              onClick={() => toggleAddForm("")}
              style={{
                padding: "4px 8px",
                backgroundColor: "#4b5563",
                color: "white",
                border: "none",
                cursor: "pointer",
              }}
            >
              ➕ Add Top-Level Key
            </button>
            {activeAddPath === "" && (
              <div style={{ marginTop: "8px" }}>
                <input
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  placeholder="Enter key name"
                  style={{ padding: "4px", marginRight: "8px" }}
                />
                <input
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  placeholder="Enter value"
                  style={{ padding: "4px", marginRight: "8px" }}
                />
                <select
                  value={newType}
                  onChange={(e) => setNewType(e.target.value)}
                  style={{ padding: "4px", marginRight: "8px" }}
                >
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                  <option value="object">Object</option>
                  <option value="array">Array</option>
                </select>
                <button
                  type="button"
                  onClick={() => handleAddKey("")}
                  disabled={!newKey || newValue === ""}
                  style={{
                    padding: "4px 8px",
                    backgroundColor:
                      !newKey || newValue === "" ? "#6b7280" : "#10b981",
                    color: "white",
                    border: "none",
                    cursor:
                      !newKey || newValue === "" ? "not-allowed" : "pointer",
                  }}
                >
                  Add
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const getFinalJson = () => {
    console.log("Final JSON:", data);
    alert(JSON.stringify(data, null, 2));
  };

  // Test with the provided input
  const inputSchema = {
    keyword: {
      description: "A string containing all details provided by the user.",
      type: "string",
    },
    newKey_1: {
      type: "object",
      description: "Return answer on the basis of query",
      properties: {
        item1: {
          type: "object",
          description: "Return answer on the basis of query",
          properties: {
            item1: {
              type: "string",
              description: "Return answer on the basis of query",
            },
          },
        },
      },
    },
  };

  console.log(JSON.stringify(convertSchemaToSimplified(inputSchema), null, 2));
  return (
    <>
      <div
        style={{
          padding: "20px",
          backgroundColor: "#111827",
          color: "#d1d5db",
          fontFamily: "monospace",
          fontSize: "14px",
        }}
      >
        <h2
          style={{ fontSize: "18px", color: "#93c5fd", marginBottom: "16px" }}
        >
          Editable JSON UI
        </h2>
        {renderObject(data)}

        <button
          type="button"
          onClick={getFinalJson}
          style={{
            padding: "6px 12px",
            backgroundColor: "#10b981",
            color: "white",
            border: "none",
            cursor: "pointer",
            marginTop: "16px",
          }}
        >
          ✅ Get Final JSON
        </button>
      </div>

      {JSON.stringify(convertSchemaToSimplified(inputSchema), null, 2)}
    </>
  );
}
